/**
 * Cart Drawer Bundle Component
 * Manages bundle drawer functionality including cart API, progress bar,
 * free gifts, slider, and popups (remove/swap/single-item/view-bundle)
 *
 * Organized into sections:
 *   1. Constructor & Init
 *   2. Cart API (centralized fetch wrappers)
 *   3. UI helpers (bubble, money, footer loading)
 *   4. Footer recalc & progress bar
 *   5. Free gift logic (lottie, auto-add, remove invalid)
 *   6. Cart item management (show, reindex, empty state)
 *   7. Cart item actions (remove, qty, delegated click)
 *   8. Add-to-bundle form
 *   9. Bundle slider (Swiper)
 *  10. Single-item checkout popup
 *  11. Remove confirmation popup
 *  12. Swap popup
 *  13. View Bundle popup
 *  14. Event delegation (drawer + document clicks)
 *  15. Drawer open & observer
 *  16. Global exposures & PubSub
 */

class CartDrawerBundle {
  constructor() {
    this.drawer = document.querySelector('cart-drawer');

    this.ROOT = window.Shopify?.routes?.root || '/';
    this.swiperInstance = null;
    this.qtyDebounceTimers = {};
    this.cartUpdateEventSeq = 0;
    this._cartBusy = false;

    this.freeGiftLottie = null;
    this.freeGiftHasBeenLocked = false;
    this.freeGiftAddTimeout = null;
    this.freeGiftAddScheduled = false;
    this.freeGiftStateDebounceTimer = null;
    this.freeGiftStateDebounceTotal = null;

    this.lastProgressStep = null;
    this.progressPhaseTimer = null;
    this.progressDebugRunSeq = 0;
    this.progressTriggerSource = 'unknown';
    this.progressForceAnimateActive = false;
    this.progressAutoShowLocked = false;

    this.swapCollectionsData = null;
    this.swapSelectedVariantId = null;
    this.swapSelectedProduct = null;

    this.viewBundlePopupSwiper = null;
    this.viewBundlePopupRefreshTimer = null;
    this.lastViewBundlePopupSyncEventId = null;
    this.progressLayoutSyncTimer = null;
    this.trayJustAutoShown = false;
    this.trayJustAutoShownTimer = null;
    this.progressBlockedUntil = 0;
    this.progressBlockedStepTimer = null;
    this.lastPaidCountFromCart = null;

    this.wasDrawerActive = false;
    this.classObserver = null;

    this.BUNDLE_CART_UPDATED_EVENT = 'bundle:cart-updated';
    this.FREE_BADGE_SVG = '<svg width="16" height="15" viewBox="0 0 16 15" fill="#ffffff" xmlns="http://www.w3.org/2000/svg"><path d="M8.17578 0.355469L10.1719 4.29297L14.5469 5.00391C14.793 5.03125 14.9844 5.19531 15.0664 5.44141C15.1484 5.6875 15.0938 5.93359 14.9023 6.09766L11.7852 9.24219L12.4688 13.6172C12.4961 13.8633 12.4141 14.1094 12.1953 14.2461C12.0039 14.3828 11.7305 14.4102 11.5117 14.3008L7.57422 12.3047L3.63672 14.3008C3.41797 14.4102 3.14453 14.3828 2.95312 14.2461C2.73438 14.1094 2.65234 13.8633 2.67969 13.6172L3.36328 9.24219L0.246094 6.09766C0.0820312 5.93359 0 5.6875 0.0820312 5.44141C0.164062 5.19531 0.355469 5.03125 0.601562 5.00391L4.97656 4.29297L7 0.355469C7.10938 0.136719 7.32812 0 7.57422 0C7.82031 0 8.03906 0.136719 8.17578 0.355469Z" fill="white"/></svg>';
    this.FREE_GIFT_ADD_DELAY_MS = 2000;
    this.LOTTIE_PLAY_PERCENT = 1;
    this.STORAGE_KEY_REMOVED_FREE_GIFTS = 'cart_removed_free_gift_ids';
    this.STORAGE_KEY_PENDING_FREE_GIFTS = 'sh_pending_free_gifts';

    this.PROGRESS_FILL_MS_FORWARD = 500;
    this.PROGRESS_FILL_MS_FORWARD_RESTORE = 270;
    this.PROGRESS_FILL_MS_BACKWARD = 250;
    this.PROGRESS_FILL_MS_BACKWARD_RESTORE = 380;
    this.PROGRESS_SLIDE_MS_FORWARD = 250;
    this.PROGRESS_SLIDE_MS_BACKWARD = 250;
    this.PROGRESS_GOAL_DOT_DELAY_MS = 1;
    this.PROGRESS_FILL_MS_FORWARD_2PLUS = 250;
    this.PROGRESS_FILL_MS_TO_100_2PLUS = 450;
    this.PROGRESS_FILL_RESTORE_2PLUS = 300;
    this.PROGRESS_SLIDE_MS_FORWARD_2PLUS = 450;
    this.PROGRESS_STEP_RATIO = 0.304;
    this.PROGRESS_STEP_RATIO_CONSTRUCTOR = 0.311;
    this.PROGRESS_DEBUG = false;

    /** Set false to silence [BundleFreeGift] console logs in production. */
    this.FREE_GIFT_DEBUG = true;

    this.DESKTOP_TRAY_MIN_WIDTH = 990;
    this.BUNDLE_TRAY_DISMISSED_COOKIE = 'sh_bundle_tray_dismissed';
    this.BUNDLE_TRAY_DISMISSED_MAX_AGE_YEARS = 1;

    this.SINGLE_ITEM_POPUP_SEEN_COOKIE = 'sh_single_item_auto_popup_dismissed';
    this.SINGLE_ITEM_POPUP_SEEN_MAX_AGE_YEARS = 1;

    this.init();
  }

  getBundleTrayDismissed() {
    if (typeof document === 'undefined' || !document.cookie) return false;
    const name = `${this.BUNDLE_TRAY_DISMISSED_COOKIE}=`;
    const parts = document.cookie.split(';');
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i].trim();
      if (part.indexOf(name) === 0) return true;
    }
    return false;
  }

  setBundleTrayDismissed() {
    const maxAge = this.BUNDLE_TRAY_DISMISSED_MAX_AGE_YEARS * 365 * 24 * 60 * 60;
    document.cookie = `${this.BUNDLE_TRAY_DISMISSED_COOKIE}=1; path=/; max-age=${maxAge}; SameSite=Lax`;
  }

  hasSingleItemPopupBeenSeen() {
    if (typeof document === 'undefined' || !document.cookie) return false;
    const name = `${this.SINGLE_ITEM_POPUP_SEEN_COOKIE}=`;
    const parts = document.cookie.split(';');
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i].trim();
      if (part.indexOf(name) === 0) return true;
    }
    return false;
  }

  setSingleItemPopupSeen() {
    const maxAge = this.SINGLE_ITEM_POPUP_SEEN_MAX_AGE_YEARS * 365 * 24 * 60 * 60;
    document.cookie = `${this.SINGLE_ITEM_POPUP_SEEN_COOKIE}=1; path=/; max-age=${maxAge}; SameSite=Lax`;
  }

  init() {
    if (!this.drawer) {
      // console.warn('Cart drawer element not found');
      return;
    }

    this.cacheElements();
    this.initBundleSlider();
    this.bindAddToBundle();
    this.bindCartItemActions();
    this.initEventDelegation();
    this.initPubSubListeners();
    this.initDrawerObserver();
    this.exposeGlobals();
    this.cleanupDiscountsOnNonCheckout();

    // Mobile/tablet: always-active state – open tray once so content loads; start at min height (132px)
    if (window.innerWidth < this.DESKTOP_TRAY_MIN_WIDTH) {
      const progressWrap = document.querySelector('.bundle-constructor__progress-wrap');
      const isInlineGoesWellTray = progressWrap && progressWrap.closest('[data-goes-well-with]');
      if (progressWrap && !isInlineGoesWellTray && this.viewBundlePopupEl && !this.getBundleTrayDismissed()) {
        setTimeout(() => {
          if (window.innerWidth >= this.DESKTOP_TRAY_MIN_WIDTH || this.getBundleTrayDismissed()) return;
          this.openViewBundleTray();
          const minH = getComputedStyle(progressWrap).getPropertyValue('--bundle-progress-wrap-min-height');
          const px = minH ? parseInt(minH, 10) : 132;
          progressWrap.style.height = `${Number.isFinite(px) ? px : 132}px`;
          progressWrap.style.overflow = 'hidden';
        }, 0);
      }
    }

    // console.log('CartDrawerBundle initialized');
  }

  cacheElements() {
    this.singleItemPopup = document.querySelector('.bundle-drawer__single-item-popup');
    this.removeConfirmEl = document.getElementById('remove-item-confirm-popup') || document.querySelector('.bundle-drawer__remove-confirm');
    this.swapPopup = document.getElementById('swap-item-popup');
    this.swapSliderEl = document.getElementById('swap-item-slider');
    this.swapSelectedTitleEl = document.getElementById('swap-item-selected-title');
    this.swapSelectedPriceEl = document.getElementById('swap-item-selected-price');
    this.viewBundlePopupEl = document.querySelector('[data-bundle-tray-panel]')
      || document.getElementById('bundle-view-tray');
    this.viewBundlePopupContentEl = document.querySelector('[data-bundle-tray-content]')
      || document.getElementById('bundle-view-tray-content');
  }

  /**
   * Cart API – centralized fetch wrappers
   */

  static safeParseJson(text, fallback = null) {
    if (!text || typeof text !== 'string') return fallback;
    try { return JSON.parse(text); } catch { return fallback; }
  }

  emitCartUpdated(source, cartData = null) {
    const payload = {
      source: source || 'bundle-drawer',
      cartData: cartData && cartData.items ? cartData : null,
      eventId: `bundle-cart-${Date.now()}-${++this.cartUpdateEventSeq}`,
    };
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS?.cartUpdate) {
      Promise.resolve(publish(PUB_SUB_EVENTS.cartUpdate, payload)).catch(() => {});
    }
    document.dispatchEvent(new CustomEvent(this.BUNDLE_CART_UPDATED_EVENT, { detail: payload }));
    return payload;
  }

  isCartBusy() { return this._cartBusy; }
  lockCart() { this._cartBusy = true; }
  unlockCart() { this._cartBusy = false; }

  /** Full-screen overlay during prepareCheckout: blocks clicks until redirect or error. */
  ensurePrepareCheckoutOverlay() {
    let el = document.getElementById('prepare-checkout-overlay');
    if (el) return el;
    el = document.createElement('div');
    el.id = 'prepare-checkout-overlay';
    el.className = 'prepare-checkout-overlay';
    el.setAttribute('hidden', '');
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML =
      '<div class="prepare-checkout-overlay__spinner loading__spinner" aria-hidden="true">' +
      '<svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66">' +
      '<circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg></div>';
    document.body.appendChild(el);
    return el;
  }

  showPrepareCheckoutOverlay() {
    const el = this.ensurePrepareCheckoutOverlay();
    this._bodyOverflowBeforePrepareCheckout = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    el.removeAttribute('hidden');
    el.setAttribute('aria-hidden', 'false');
    document.body.setAttribute('aria-busy', 'true');
  }

  hidePrepareCheckoutOverlay() {
    const el = document.getElementById('prepare-checkout-overlay');
    if (!el) return;
    el.setAttribute('hidden', '');
    el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = this._bodyOverflowBeforePrepareCheckout || '';
    document.body.removeAttribute('aria-busy');
  }

  /** Debug logging for free-gift tier / mismatch flow (see FREE_GIFT_DEBUG). */
  _freeGiftLog(message, payload = null) {
    if (!this.FREE_GIFT_DEBUG) return;
    if (payload != null) console.log('[BundleFreeGift]', message, payload);
    else console.log('[BundleFreeGift]', message);
  }

  cartAdd(items, label = '') {
    const caller = new Error().stack;
    console.log(`%c[CartAPI] cart/add.js — ${label}`, 'color:#e6a020;font-weight:bold', { items, caller });
    this.drawer.classList.add('loaded');
    const url = `${this.ROOT}cart/add.js`;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(Array.isArray(items) ? { items } : items),
    }).then(async (res) => {
      const data = CartDrawerBundle.safeParseJson(await res.text());
      console.log(`%c[CartAPI] cart/add.js — ${label} ← ${res.status}`, 'color:#e6a020', { data });
      if (!res.ok) throw new Error(data?.description || data?.message || `Add failed (${res.status})`);
      this.emitCartUpdated(`bundle-drawer:${label || 'add'}`);
      return data;
    }).finally(() => { this.drawer.classList.remove('loaded'); });
  }

  cartAddFormData(formData, label = '') {
    console.log(`%c[CartAPI] cart/add.js (FormData) — ${label}`, 'color:#e6a020;font-weight:bold');
    this.drawer.classList.add('loaded');
    const url = `${this.ROOT}cart/add.js`;
    return fetch(url, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      credentials: 'same-origin',
      body: formData,
    }).then(async (res) => {
      const data = CartDrawerBundle.safeParseJson(await res.text(), {});
      console.log(`%c[CartAPI] cart/add.js (FormData) — ${label} ← ${res.status}`, 'color:#e6a020', { data });
      if (!res.ok) throw new Error(data?.description || data?.message || `Add failed (${res.status})`);
      this.emitCartUpdated(`bundle-drawer:${label || 'add-form'}`);
      return data;
    }).finally(() => { this.drawer.classList.remove('loaded'); });
  }

  cartChange(line, quantity, label = '') {
    const caller = new Error().stack;
    console.log(`%c[CartAPI] cart/change.js — ${label}`, 'color:#c080ff;font-weight:bold', { line, quantity, caller });
    this.drawer.classList.add('loaded');
    const url = `${this.ROOT}cart/change.js`;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ line, quantity }),
    }).then(async (res) => {
      const data = CartDrawerBundle.safeParseJson(await res.text());
      console.log(`%c[CartAPI] cart/change.js — ${label} ← ${res.status}`, 'color:#c080ff', { data });
      if (!res.ok) throw new Error(data?.message || `Change failed (${res.status})`);
      this.emitCartUpdated(`bundle-drawer:${label || 'change'}`, data);
      return data;
    }).finally(() => { this.drawer.classList.remove('loaded'); });
  }

  cartChangeById(variantId, quantity, label = '') {
    const caller = new Error().stack;
    console.log(`%c[CartAPI] cart/change.js (byId) — ${label}`, 'color:#c080ff;font-weight:bold', { variantId, quantity, caller });
    this.drawer.classList.add('loaded');
    const url = `${this.ROOT}cart/change.js`;
    const id = String(variantId);
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id, quantity }),
    }).then(async (res) => {
      const data = CartDrawerBundle.safeParseJson(await res.text());
      console.log(`%c[CartAPI] cart/change.js (byId) — ${label} ← ${res.status}`, 'color:#c080ff', { data });
      if (!res.ok) throw new Error(data?.message || `Change failed (${res.status})`);
      this.emitCartUpdated(`bundle-drawer:${label || 'change-by-id'}`, data);
      return data;
    }).finally(() => { this.drawer.classList.remove('loaded'); });
  }

  cartGet(label = '') {
    const caller = new Error().stack;
    console.log(`%c[CartAPI] cart.js (GET) — ${label}`, 'color:#50c8ff;font-weight:bold', { caller });
    const url = `${this.ROOT}cart.js`;
    return fetch(url, { credentials: 'same-origin' })
      .then(async (res) => {
        const data = CartDrawerBundle.safeParseJson(await res.text());
        console.log(`%c[CartAPI] cart.js (GET) — ${label} ← ${res.status}`, 'color:#50c8ff', { itemCount: data?.item_count, totalPrice: data?.total_price });
        return data;
      });
  }

  cartSectionHtml(label = '') {
    console.log(`%c[CartAPI] sectionHtml (GET) — ${label}`, 'color:#80ff80;font-weight:bold', { caller: new Error().stack });
    const url = `${window.Shopify?.routes?.cart_url || '/cart'}?section_id=cart-drawer`;
    return fetch(url, { credentials: 'same-origin' })
      .then(async (res) => {
        console.log(`%c[CartAPI] sectionHtml (GET) — ${label} ← ${res.status}`, 'color:#80ff80');
        return res.text();
      });
  }

  /**
   * Compute desired discount code string for a given paid item count.
   * @param {number} paidCount
   * @returns {string} Comma-joined discount codes (or empty string to clear).
   */
  computeDesiredDiscountString(paidCount) {
    const bundleEl = this.drawer?.querySelector('.bundle-drawer');
    const freeCode = (bundleEl?.dataset?.bundleFreeShippingCode || '').trim();
    const discountCode = (bundleEl?.dataset?.bundleDiscountCode || '').trim();
    const codes = [];
    if (paidCount >= 2 && freeCode) codes.push(freeCode);
    if (paidCount >= 3 && discountCode) codes.push(discountCode);
    return codes.join(',');
  }

  /**
   * On non-checkout pages: remove any discount codes + free gifts left in the
   * real Shopify Cart from a previous prepareCheckout() call (e.g. user pressed
   * "back" from checkout). Runs once on page load.
   */
  cleanupDiscountsOnNonCheckout() {
    if (window.location.pathname.includes('/checkout')) return;

    this.cartGet('cleanup-non-checkout').then((cart) => {
      if (!cart?.items) return;

      const freeVariantSet = this.getFreeGiftVariantIdSet();
      const updates = {};
      let needsCleanup = false;

      cart.items.forEach((item) => {
        const vid = String(item.variant_id);
        if (freeVariantSet.has(vid)) {
          updates[vid] = 0;
          needsCleanup = true;
        }
      });

      const hasDiscount = cart.cart_level_discount_applications?.length > 0
        || (typeof cart.discount === 'string' && cart.discount.length > 0);

      if (needsCleanup || hasDiscount) {
        this._freeGiftLog('cleanupDiscountsOnNonCheckout: clearing discounts + free gifts', {
          freeGiftsToRemove: Object.keys(updates),
          hasDiscount,
        });
        return this.cartBatchUpdate(updates, '').then((data) => {
          if (needsCleanup) {
            Object.keys(updates).forEach((vid) => {
              const el = this.drawer.querySelector(`.bundle-drawer__cart-item[data-variant-id="${vid}"]`);
              if (el) el.remove();
            });
            this.reindexCartItems();
            this.recalcFooter();
            if (data) this.updateCartBubble(this.getPaidItemCount(data));
          }
        });
      }
    }).catch(() => {});
  }

  /**
   * Single cart/update.js call that can set quantities AND discount at once.
   * @param {Object} updates - Map of variant_id -> quantity.
   * @param {string} [discount] - Discount string (omit to leave unchanged).
   * @returns {Promise<object>} Cart data from response.
   */
  cartBatchUpdate(updates, discount) {
    const caller = new Error().stack;
    console.log(`%c[CartAPI] cart/update.js (batch) — cartBatchUpdate`, 'color:#ff50a0;font-weight:bold', { updates, discount, caller });
    this.drawer.classList.add('loaded');
    const payload = {};
    if (updates && Object.keys(updates).length) payload.updates = updates;
    if (typeof discount === 'string') payload.discount = discount;
    return fetch(`${this.ROOT}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    }).then(async (res) => {
      const data = CartDrawerBundle.safeParseJson(await res.text());
      console.log(`%c[CartAPI] cart/update.js (batch) ← ${res.status}`, 'color:#ff50a0', { itemCount: data?.item_count, totalPrice: data?.total_price, payload });
      if (!res.ok) throw new Error(data?.message || `Batch update failed (${res.status})`);
      return data;
    }).finally(() => { this.drawer.classList.remove('loaded'); });
  }

  /**
   * Add pending free gifts + apply discount codes + redirect to checkout.
   * This is the ONLY place where free gifts enter the real Shopify Cart.
   * @param {string} [redirectUrl='/checkout']
   */
  async prepareCheckout(redirectUrl = '/checkout') {
    if (this._prepareCheckoutBusy) return;
    this._prepareCheckoutBusy = true;
    try {
      this.showPrepareCheckoutOverlay();
      this.lockCart();
      this.setFooterLoading(true);

      const pending = this.getPendingFreeGifts();
      const cart = await this.cartGet('prepareCheckout');

      const inCart = new Map();
      if (cart?.items) {
        cart.items.forEach((item) => {
          inCart.set(String(item.variant_id), item.quantity || 1);
        });
      }

      const toAdd = [];
      const toFixQty = {};
      [pending.addon, pending.game].filter(Boolean).forEach((vid) => {
        const existing = inCart.get(String(vid)) || 0;
        if (existing === 0) {
          toAdd.push({ id: parseInt(vid, 10), quantity: 1 });
        } else if (existing > 1) {
          toFixQty[String(vid)] = 1;
        }
      });

      if (toAdd.length) {
        await this.cartAdd(toAdd, 'checkout: add free gifts');
      }

      let paidCount = 0;
      this.drawer.querySelectorAll('.bundle-drawer__cart-item').forEach((el) => {
        if (el.dataset.freeGift !== 'true') paidCount += parseInt(el.querySelector('.bundle-drawer__qty-value')?.textContent || '1', 10);
      });
      const discount = this.computeDesiredDiscountString(paidCount);
      if (discount || Object.keys(toFixQty).length) {
        await this.cartBatchUpdate(toFixQty, discount || '');
      }

      this.clearPendingFreeGifts();
      window.location.href = redirectUrl;
    } catch (err) {
      console.error('[Cart] prepareCheckout error:', err);
      this.hidePrepareCheckoutOverlay();
      this._prepareCheckoutBusy = false;
      this.setFooterLoading(false);
      this.unlockCart();
      window.location.href = redirectUrl;
    }
  }

  /**
   * Validate cart before checkout: ensure paid items present, total > 0,
   * and free gift limits are respected (max 1 addon + max 1 game).
   * Removes excess / invalid free items and refreshes the drawer.
   * @returns {Promise<boolean>} true = checkout allowed, false = blocked.
   */
  async validateCartBeforeCheckout() {
    try {
      const cart = await this.cartGet('checkout-validation');
      if (!cart?.items?.length) return false;

      const addonSet = this.getFreeAddonVariantIdSet();
      const gamesSet = this.getFreeGamesVariantIdSet();
      const allFreeSet = this.getFreeGiftVariantIdSet();

      let paidCount = 0;
      let addonCount = 0;
      let gameCount = 0;
      const updates = {};

      cart.items.forEach((item) => {
        const vid = String(item.variant_id);
        if (addonSet.has(vid)) {
          addonCount++;
          if (addonCount > 1) updates[item.key] = 0;
        } else if (gamesSet.has(vid)) {
          gameCount++;
          if (gameCount > 1) updates[item.key] = 0;
        } else if (allFreeSet.has(vid) || item.final_price === 0) {
          updates[item.key] = 0;
        } else {
          paidCount += item.quantity || 1;
        }
      });

      if (paidCount < 2) {
        cart.items.forEach((item) => {
          const vid = String(item.variant_id);
          if (addonSet.has(vid) || gamesSet.has(vid) || allFreeSet.has(vid) || item.final_price === 0) {
            updates[item.key] = 0;
          }
        });
      }

      if (Object.keys(updates).length) {
        const res = await fetch(`${this.ROOT}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates }),
        });
        const updated = await res.json();
        const newPaid = this.getPaidItemCount(updated);
        this.updateCartBubble(newPaid);
        if (!updated?.items?.length || updated.total_price === 0) {
          this.resetToEmptyState();
          return false;
        }
        return true;
      }

      if (paidCount === 0 || cart.total_price === 0) {
        this.resetToEmptyState();
        this.updateCartBubble(0);
        return false;
      }
      return true;
    } catch (err) {
      console.error('[Cart] validateCartBeforeCheckout error:', err);
      return true;
    }
  }

  /**
   * UI Helpers
   */

  /** Count only paid items (final_price > 0). Falls back to item_count if cart has no items array. */
  getPaidItemCount(cartData) {
    if (!cartData?.items?.length) return cartData?.item_count ?? 0;
    return cartData.items.reduce((sum, item) => sum + ((item.final_price > 0) ? (item.quantity || 1) : 0), 0);
  }

  updateCartBubble(itemCount) {
    const cartButton = document.getElementById('cart-button');
    if (!cartButton) return;
    const maxItems = parseInt(cartButton.dataset.maxItems, 10) || 2;
    const displayMax = itemCount > maxItems ? itemCount : maxItems;
    const countEl = cartButton.querySelector('.header__cart-count');

    if (countEl) {
      if (itemCount > 2) {
        countEl.textContent = `${itemCount}`;
      } else {
        countEl.textContent = `${itemCount}/${displayMax}`;
      }
    }
    cartButton.classList.toggle('header__action-button--cart--full', itemCount >= 2);
  }

  formatMoney(cents) {
    return `$${(cents / 100).toFixed(2)} USD`;
  }

  setFooterLoading(loading) {
    const footer = this.drawer.querySelector('.bundle-drawer__footer');
    if (!footer) return;
    footer.classList.toggle('bundle-drawer__footer--loading', loading);
  }

  /**
   * Footer Recalc & Progress Bar
   */

  recalcFooter() {
    const footer = this.drawer.querySelector('.bundle-drawer__footer');
    if (!footer) return;

    const items = this.drawer.querySelectorAll('.bundle-drawer__cart-item');
    if (!items.length) { footer.classList.add('hidden'); return; }

    const freeVariantSet = this.getFreeGiftVariantIdSet();
    let totalItems = 0, paidItems = 0, subtotalCents = 0;
    items.forEach((item) => {
      const price = parseInt(item.dataset.itemPrice, 10) || 0;
      const qtyEl = item.querySelector('.bundle-drawer__qty-value');
      const qty = (item.dataset.freeGift === 'true' || freeVariantSet.has(String(item.dataset.variantId))) ? 1 : parseInt(qtyEl?.textContent || '1', 10);
      totalItems += qty;
      const isFree = freeVariantSet.has(String(item.dataset.variantId));
      if (!isFree) {
        paidItems += qty;
        subtotalCents += price * qty;
      }
    });

    footer.classList.remove('hidden');

    const labelEl = footer.querySelector('.bundle-drawer__totals-label');
    if (labelEl) {
      const plural = totalItems === 1 ? 'item' : 'items';
      labelEl.innerHTML = `Subtotal (<span class="bundle-drawer__totals-count">${totalItems}</span> ${plural})`;
    }

    const subtotalEl = footer.querySelector('.bundle-drawer__subtotal-value');
    if (subtotalEl) subtotalEl.textContent = this.formatMoney(subtotalCents).replace(' USD', '');

    const addonSet = this.getFreeAddonVariantIdSet();
    const gamesSet = this.getFreeGamesVariantIdSet(this.getEffectiveFreeGameTier());
    let hasFreeAddonInCart = false;
    let hasFreeGameInCart = false;
    items.forEach((item) => {
      const vid = String(item.dataset.variantId || '');
      if (addonSet.has(vid)) hasFreeAddonInCart = true;
      if (gamesSet.has(vid)) hasFreeGameInCart = true;
    });

    const toggleFooterRow = (row, show) => {
      if (!row) return;
      if (show) {
        row.classList.remove('hidden');
        row.removeAttribute('aria-hidden');
      } else {
        row.classList.add('hidden');
        row.setAttribute('aria-hidden', 'true');
      }
    };

    toggleFooterRow(footer.querySelector('.bundle-drawer__totals-row--free-shipping'), paidItems >= 2);
    toggleFooterRow(footer.querySelector('.bundle-drawer__totals-row--free-addon'), hasFreeAddonInCart);
    toggleFooterRow(footer.querySelector('.bundle-drawer__totals-row--free-game'), hasFreeGameInCart);

    const bundleRoot = footer.closest('.bundle-drawer');
    const discountPercent = parseInt(bundleRoot?.dataset?.bundleDiscountPercent || '0', 10);
    const discountCode = (bundleRoot?.dataset?.bundleDiscountCode || '').trim();
    const showBundleDiscount = paidItems >= 3 && Boolean(discountCode) && discountPercent > 0;
    const discountCents = showBundleDiscount ? Math.round((subtotalCents * discountPercent) / 100) : 0;
    const discountValueEl = footer.querySelector('.bundle-drawer__bundle-discount-value');
    if (discountValueEl) {
      discountValueEl.textContent = showBundleDiscount
        ? `-${this.formatMoney(discountCents).replace(' USD', '')}`
        : '—';
    }
    toggleFooterRow(footer.querySelector('.bundle-drawer__totals-row--bundle-discount'), showBundleDiscount);

    const totalAfterDiscountCents = Math.max(0, subtotalCents - discountCents);
    const totalEl = footer.querySelector('.bundle-drawer__total-value');
    if (totalEl) totalEl.textContent = this.formatMoney(showBundleDiscount ? totalAfterDiscountCents : subtotalCents).replace(' USD', '');

    const checkoutText = footer.querySelector('.bundle-drawer__checkout-text');
    if (checkoutText) {
      checkoutText.textContent = totalItems === 1 ? 'SINGLE ITEM CHECKOUT' : 'CHECKOUT';
    }

    this.scheduleUpdateFreeGiftState(totalItems);
    this.setProgressTriggerSource('recalcFooter');
    if (this.PROGRESS_DEBUG) {
      console.log('[BundleProgress] trigger:recalcFooter', { totalItems, paidItems });
    }
    if (this.lastPaidCountFromCart != null && paidItems !== this.lastPaidCountFromCart) return;
    this.advanceProgress(Math.min(paidItems, 20));
  }

  getOrdinal(n) {
    const ordinals = ['zeroth', 'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth',
      'eleventh', 'twelfth', 'thirteenth', 'fourteenth', 'fifteenth', 'sixteenth', 'seventeenth', 'eighteenth', 'nineteenth', 'twentieth'];
    if (n >= 1 && n <= ordinals.length) return ordinals[n];
    const last = n % 10;
    const suffix = (last === 1 && n % 100 !== 11) ? 'st' : (last === 2 && n % 100 !== 12) ? 'nd' : (last === 3 && n % 100 !== 13) ? 'rd' : 'th';
    return n + suffix;
  }

  setProgressTriggerSource(source) {
    this.progressTriggerSource = source || 'unknown';
  }

  logProgress(runId, stage, extra = {}) {
    if (!this.PROGRESS_DEBUG) return;
    console.log(`[BundleProgress][run:${runId}] ${stage}`, extra);
  }

  clearProgressPhaseTimer() {
    if (this.progressPhaseTimer) {
      this.logProgress('n/a', 'clearProgressPhaseTimer', { hadTimer: true });
    }
    if (!this.progressPhaseTimer) return;
    clearTimeout(this.progressPhaseTimer);
    this.progressPhaseTimer = null;
    this.progressForceAnimateActive = false;
    this.progressAutoShowLocked = false;
  }

  setupProgressMetrics(progress) {
    progress.style.setProperty('--progress-dot-count', '21');
    const dotContainer = progress.querySelector('.bundle-drawer__progress-dot-container');
    const lineWrapper = progress.querySelector('.bundle-drawer__progress-line2-wrapper');
    if (!dotContainer) return;
    if (!lineWrapper) return;
    const isConstructor = Boolean(progress.closest('.bundle-constructor__progress-wrap'));
    const stepRatio = isConstructor ? this.PROGRESS_STEP_RATIO_CONSTRUCTOR : this.PROGRESS_STEP_RATIO;
    const stepSize = Math.max(1, lineWrapper.clientWidth * stepRatio);
    progress.style.setProperty('--progress-step-size', `${stepSize}px`);
    dotContainer.querySelectorAll('.bundle-drawer__progress-dot2').forEach((dot) => {
      const dotIndex = parseInt(dot.dataset.stepIndex, 10);
      if (!Number.isFinite(dotIndex)) return;
      dot.style.setProperty('--dot-index', String(dotIndex));
    });
  }

  setProgressVisualState(progress, { fillPercent, shiftSteps, animateFill, animateSlide, fillDurationMs, slideDurationMs }) {
    this.setupProgressMetrics(progress);
    progress.style.setProperty('--progress-fill', `${fillPercent}%`);
    progress.style.setProperty('--progress-shift-steps', String(Math.max(0, shiftSteps)));
    const fillMs = animateFill ? (fillDurationMs ?? this.PROGRESS_FILL_MS_FORWARD) : 0;
    progress.style.setProperty('--progress-fill-duration', `${fillMs}ms`);
    const slideMs = animateSlide ? (slideDurationMs ?? this.PROGRESS_SLIDE_MS_FORWARD) : 0;
    progress.style.setProperty('--progress-slide-duration', `${slideMs}ms`);
  }

  getFillPercentToCurrentDot(step) {
    if (step === 1) return 33;
    return 63;
  }

  renderProgressDots(progress, step, options = {}) {
    const normalizedStep = Math.max(0, step);
    const currentIndex = normalizedStep === 0 ? 1 : normalizedStep;
    const goalIndex = currentIndex + 1;
    const skipArchivedFade = options.skipArchivedFade === true;
    const hideGoal = options.hideGoal === true;
    const isConstructorBar = Boolean(progress.closest('.bundle-constructor__progress-wrap'));

    progress.querySelectorAll('.bundle-drawer__progress-dot2').forEach((dot) => {
      dot.classList.remove(
        'is-start',
        'is-complete',
        'is-current',
        'is-goal',
        'is-before-current',
        'is-after-goal',
        'is-archived-complete',
        'is-star-goal',
        'is-star-current',
        'is-number-current',
        'is-constructor-hidden',
        'is-goal-delayed'
      );

      const dotIndex = parseInt(dot.dataset.stepIndex, 10);
      if (!Number.isFinite(dotIndex)) return;

      if (isConstructorBar) {
        const showIndexSet = new Set([0, currentIndex, goalIndex]);
        if (!showIndexSet.has(dotIndex)) {
          dot.classList.add('is-constructor-hidden');
          return;
        }
      }

      if (dotIndex === 0) {
        dot.classList.add('is-start');
        return;
      }

      if (dotIndex < normalizedStep || (normalizedStep >= 3 && dotIndex === currentIndex)) dot.classList.add('is-complete');
      if (!isConstructorBar && !skipArchivedFade && dotIndex > 0 && dotIndex < currentIndex - 1) dot.classList.add('is-archived-complete');
      if (normalizedStep >= 1 && dotIndex === currentIndex) dot.classList.add('is-current');
      if (dotIndex === goalIndex) {
        if (hideGoal) dot.classList.add('is-goal-delayed');
        else dot.classList.add('is-goal');
      }
      if (!isConstructorBar && !skipArchivedFade && dotIndex === currentIndex - 1 && dotIndex > 0) dot.classList.add('is-before-current');
      if (!isConstructorBar && dotIndex > goalIndex) dot.classList.add('is-after-goal');

      if (normalizedStep <= 1 && dotIndex === 2) dot.classList.add('is-star-goal');
      if (normalizedStep === 2 && dotIndex === 2) dot.classList.add('is-star-current');
      if (normalizedStep >= 3 && dotIndex === currentIndex) dot.classList.add('is-number-current');
    });
  }

  advanceProgress(step, opts = {}) {
    const now = Date.now();
    if (now < this.progressBlockedUntil) {
      const remaining = this.progressBlockedUntil - now;
      if (this.PROGRESS_DEBUG) console.log('[BundleProgress] advanceProgress:blocked-by-timer', { step, remaining, forceAnimate: opts?.forceAnimate, trigger: this.progressTriggerSource });
      clearTimeout(this.progressBlockedStepTimer);
      this.progressBlockedStepTimer = setTimeout(() => {
        this.progressBlockedStepTimer = null;
        this.advanceProgress(step, opts);
      }, remaining);
      return;
    }
    if (this.progressAutoShowLocked && !opts?.forceAnimate) {
      if (this.PROGRESS_DEBUG) console.log('[BundleProgress] advanceProgress:blocked-by-autoshow-lock', { step, trigger: this.progressTriggerSource });
      return;
    }
    const runId = ++this.progressDebugRunSeq;
    const allProgress = document.querySelectorAll('.bundle-drawer__progress');
    if (!allProgress.length) {
      this.logProgress(runId, 'skip:no-progress-elements', { trigger: this.progressTriggerSource, step });
      return;
    }
    const progress = allProgress[0];
    allProgress.forEach((p) => p.classList.remove('bundle-drawer__progress--pending'));
    const normalizedStep = Math.max(0, Math.min(20, parseInt(step, 10) || 0));
    const stepStr = String(normalizedStep);
    let previousStep = this.lastProgressStep == null ? (parseInt(progress.dataset.step, 10) || 0) : this.lastProgressStep;
    let increasedByOne = normalizedStep === previousStep + 1;
    const decreasedByOne = normalizedStep === previousStep - 1;
    let currentShift = Math.max(0, previousStep - 1);
    const nextShift = Math.max(0, normalizedStep - 1);
    const forceAnimate = Boolean(opts?.forceAnimate);
    if (forceAnimate && normalizedStep >= 1 && normalizedStep === previousStep) {
      previousStep = normalizedStep - 1;
      increasedByOne = true;
      currentShift = Math.max(0, previousStep - 1);
      this.progressForceAnimateActive = true;
    }
    const trigger = this.progressTriggerSource;
    this.progressTriggerSource = 'unknown';
    this.logProgress(runId, 'start', {
      trigger,
      inputStep: step,
      normalizedStep,
      previousStep,
      lastProgressStep: this.lastProgressStep,
      increasedByOne,
      decreasedByOne,
      progressPhaseTimerActive: this.progressPhaseTimer != null,
      forceAnimate,
      forceAnimateActive: this.progressForceAnimateActive,
    });

    const applyToAll = (fn) => allProgress.forEach((p) => fn(p));

    if (this.progressPhaseTimer != null && this.lastProgressStep != null && normalizedStep === this.lastProgressStep) {
      this.logProgress(runId, 'branch:same-step-while-timer-active-clear-for-resync', { normalizedStep, trigger });
      this.clearProgressPhaseTimer();
    }

    allProgress.forEach((p) => { p.dataset.step = stepStr; });
    this.clearProgressPhaseTimer();

    if (decreasedByOne && previousStep === 1) {
      this.logProgress(runId, 'branch:decrease-1-to-0', { previousStep, normalizedStep });
      this.lastProgressStep = normalizedStep;
      applyToAll((p) => { this.renderProgressDots(p, 0); p.dataset.phase = 'filling'; this.setProgressVisualState(p, { fillPercent: 33, shiftSteps: 0, animateFill: false, animateSlide: false }); });
      requestAnimationFrame(() => {
        this.logProgress(runId, 'phase:decrease-1-to-0:raf-start');
        applyToAll((p) => this.setProgressVisualState(p, { fillPercent: 0, shiftSteps: 0, animateFill: true, animateSlide: false, fillDurationMs: this.PROGRESS_FILL_MS_BACKWARD }));
        this.progressPhaseTimer = setTimeout(() => {
          this.logProgress(runId, 'phase:decrease-1-to-0:timer-complete');
          allProgress.forEach((p) => { p.dataset.phase = 'idle'; this.renderProgressDots(p, 0); this.setProgressVisualState(p, { fillPercent: 0, shiftSteps: 0, animateFill: false, animateSlide: false }); });
          this.logProgress(runId, 'finish:decrease-1-to-0', { normalizedStep: 0 });
        }, this.PROGRESS_FILL_MS_BACKWARD + 50);
      });
    } else if (decreasedByOne && previousStep >= 2) {
      this.logProgress(runId, 'branch:decrease-by-one-from-2plus', { previousStep, normalizedStep });
      this.lastProgressStep = normalizedStep;
      applyToAll((p) => { this.renderProgressDots(p, normalizedStep); this.setProgressVisualState(p, { fillPercent: 33, shiftSteps: currentShift, animateFill: false, animateSlide: false }); });
      this.progressPhaseTimer = setTimeout(() => {
        this.logProgress(runId, 'phase:decrease-2plus:phase2-fill-33-to-0', { currentShift });
        applyToAll((p) => { p.dataset.phase = 'filling'; this.setProgressVisualState(p, { fillPercent: 0, shiftSteps: currentShift, animateFill: true, animateSlide: false, fillDurationMs: this.PROGRESS_FILL_MS_BACKWARD }); });
        this.progressPhaseTimer = setTimeout(() => {
          this.logProgress(runId, 'phase:decrease-2plus:phase3-slide-back-and-restore', { nextShift });
          applyToAll((p) => { p.dataset.phase = 'sliding'; this.setProgressVisualState(p, { fillPercent: 33, shiftSteps: nextShift, animateFill: true, animateSlide: true, fillDurationMs: this.PROGRESS_FILL_MS_FORWARD_RESTORE, slideDurationMs: this.PROGRESS_SLIDE_MS_BACKWARD }); });
          this.progressPhaseTimer = setTimeout(() => {
            this.logProgress(runId, 'phase:decrease-2plus:finish-idle', { normalizedStep });
            allProgress.forEach((p) => { p.dataset.phase = 'idle'; this.setProgressVisualState(p, { fillPercent: 33, shiftSteps: nextShift, animateFill: false, animateSlide: false }); });
            this.logProgress(runId, 'finish:decrease-2plus', { normalizedStep });
          }, this.PROGRESS_SLIDE_MS_BACKWARD + 50);
        }, this.PROGRESS_FILL_MS_BACKWARD + 50);
      }, 50);
    } else if (this.lastProgressStep == null || normalizedStep <= previousStep || !increasedByOne) {
      this.logProgress(runId, 'branch:snap-sync-no-forward-animation', {
        previousStep,
        normalizedStep,
        reason: this.lastProgressStep == null ? 'initial' : (!increasedByOne ? 'jump-or-no-increase' : 'decrease-or-equal'),
      });
      applyToAll((p) => { this.renderProgressDots(p, normalizedStep); p.dataset.phase = 'idle'; this.setProgressVisualState(p, { fillPercent: normalizedStep === 0 ? 0 : 33, shiftSteps: nextShift, animateFill: false, animateSlide: false }); });
      this.lastProgressStep = normalizedStep;
      this.logProgress(runId, 'finish:snap-sync', { normalizedStep });
    } else if (normalizedStep === 1) {
      this.logProgress(runId, 'branch:forward-to-1', { previousStep, normalizedStep });
      applyToAll((p) => { this.renderProgressDots(p, 0); p.dataset.phase = 'filling'; this.setProgressVisualState(p, { fillPercent: 33, shiftSteps: 0, animateFill: true, animateSlide: false, fillDurationMs: this.PROGRESS_FILL_MS_FORWARD }); });
      this.progressPhaseTimer = setTimeout(() => {
        this.logProgress(runId, 'phase:forward-to-1:timer-complete');
        this.progressForceAnimateActive = false;
        this.progressAutoShowLocked = false;
        allProgress.forEach((p) => { p.dataset.phase = 'idle'; this.renderProgressDots(p, normalizedStep); this.setProgressVisualState(p, { fillPercent: 33, shiftSteps: 0, animateFill: false, animateSlide: false }); });
        this.logProgress(runId, 'finish:forward-to-1', { normalizedStep });
      }, this.PROGRESS_FILL_MS_FORWARD + 50);
      this.lastProgressStep = normalizedStep;
    } else {
      this.logProgress(runId, 'branch:forward-2plus', { previousStep, normalizedStep });
      this.lastProgressStep = normalizedStep;
      const dotPercent = this.getFillPercentToCurrentDot(normalizedStep);
      applyToAll((p) => { p.dataset.phase = 'filling'; this.setProgressVisualState(p, { fillPercent: dotPercent, shiftSteps: currentShift, animateFill: true, animateSlide: false, fillDurationMs: this.PROGRESS_FILL_MS_FORWARD_2PLUS }); });
      this.progressPhaseTimer = setTimeout(() => {
        this.logProgress(runId, 'phase:forward-2plus:phase2-skip-dot-change', { dotPercent, currentShift });
        this.progressPhaseTimer = setTimeout(() => {
          this.logProgress(runId, 'phase:forward-2plus:phase3-fill-to-100');
          applyToAll((p) => { p.dataset.phase = 'filling'; this.setProgressVisualState(p, { fillPercent: 100, shiftSteps: currentShift, animateFill: true, animateSlide: false, fillDurationMs: this.PROGRESS_FILL_MS_TO_100_2PLUS }); });
          this.progressPhaseTimer = setTimeout(() => {
            this.logProgress(runId, 'phase:forward-2plus:phase4-slide-and-restore', { nextShift });
            applyToAll((p) => { this.renderProgressDots(p, normalizedStep); p.dataset.phase = 'sliding'; this.setProgressVisualState(p, { fillPercent: 33, shiftSteps: nextShift, animateFill: true, animateSlide: true, fillDurationMs: this.PROGRESS_FILL_RESTORE_2PLUS, slideDurationMs: this.PROGRESS_SLIDE_MS_FORWARD_2PLUS }); });
            this.progressPhaseTimer = setTimeout(() => {
              this.progressForceAnimateActive = false;
              this.progressAutoShowLocked = false;
              allProgress.forEach((p) => { p.dataset.phase = 'idle'; this.renderProgressDots(p, normalizedStep); this.setProgressVisualState(p, { fillPercent: 33, shiftSteps: nextShift, animateFill: false, animateSlide: false }); });
              this.logProgress(runId, 'finish:forward-2plus', { normalizedStep });
            }, this.PROGRESS_SLIDE_MS_FORWARD_2PLUS + 16);
          }, this.PROGRESS_FILL_MS_TO_100_2PLUS + 16);
        }, 16);
      }, this.PROGRESS_FILL_MS_FORWARD_2PLUS + 16);
    }

    const bundleDrawer = document.querySelector('.bundle-drawer');
    const percent = parseInt(bundleDrawer?.dataset?.bundleDiscountPercent || '0', 10);
    const discountLabel = percent > 0 ? `${percent}%` : '';
    const progressTextHtml = normalizedStep >= 3 ? `Add a ${this.getOrdinal(normalizedStep + 1)} item` : normalizedStep >= 2 ? `<strong>KEEP GOING:</strong> Add 1 more for ${discountLabel} off total order` : normalizedStep >= 1 ? `Add a ${this.getOrdinal(normalizedStep + 1)} item to get <strong>FREE SHIT</strong>` : 'Add an item to <strong>get started!</strong>';
    const progressTextHtmlBundleTray = normalizedStep >= 3 ? `Add a ${this.getOrdinal(normalizedStep + 1)} item` : normalizedStep >= 2 ? `<strong>KEEP GOING:</strong> Add 1 more for ${discountLabel} off total order` : normalizedStep >= 1 ? `Add a ${this.getOrdinal(normalizedStep + 1)} item to get <strong>FREE SHIT!</strong>` : 'Add an item to <strong>GET STARTED</strong>';
    allProgress.forEach((p) => {
      const progressText = p.querySelector('.bundle-drawer__progress-text');
      if (p.closest('.bundle-constructor__progress-wrap')) { progressText.innerHTML = progressTextHtmlBundleTray; }
      else if (progressText) { progressText.innerHTML = progressTextHtml; }
    });
  }

  /**
   * Free Gift Logic
   */

  getRemovedFreeGiftIds() {
    try {
      const raw = sessionStorage.getItem(this.STORAGE_KEY_REMOVED_FREE_GIFTS);
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return new Set(Array.isArray(arr) ? arr.map(String) : []);
    } catch {
      return new Set();
    }
  }

  addRemovedFreeGiftId(variantId) {
    if (!variantId) return;
    const set = this.getRemovedFreeGiftIds();
    set.add(String(variantId));
    try {
      sessionStorage.setItem(this.STORAGE_KEY_REMOVED_FREE_GIFTS, JSON.stringify([...set]));
    } catch (e) {}
  }

  clearRemovedFreeGiftIds() {
    try {
      sessionStorage.removeItem(this.STORAGE_KEY_REMOVED_FREE_GIFTS);
    } catch (e) {}
  }

  /** Normalize metafield / data-attribute tier strings (tier-2, Tier 2, tier_2, etc.) */
  normalizeProductTier(raw) {
    const s = String(raw ?? '')
      .trim()
      .toLowerCase()
      .replace(/_/g, '-')
      .replace(/\s+/g, '');
    if (s === 'tier-2' || s === 'tier2') return 'tier-2';
    return 'tier-1';
  }

  /**
   * Resolve product_tier for a variant (drawer slider, bundle constructor card, or swap JSON).
   */
  resolveVariantProductTier(variantId) {
    if (!variantId) return 'tier-1';
    const vid = String(variantId);
    const slide = this.drawer?.querySelector(`.bundle-drawer__slide[data-variant-id="${vid}"]`);
    if (this.normalizeProductTier(slide?.dataset.productTier) === 'tier-2') return 'tier-2';
    const card = document.querySelector(`.bundle-constructor-card[data-variant-id="${vid}"]`);
    if (this.normalizeProductTier(card?.dataset.productTier) === 'tier-2') return 'tier-2';
    const data = this.getSwapCollectionsData();
    const row = data?.cart_drawer?.find((p) => String(p.variant_id) === vid);
    if (this.normalizeProductTier(row?.product_tier) === 'tier-2') return 'tier-2';
    return 'tier-1';
  }

  /**
   * Tier for free-game eligibility: tier-2 only when every paid line is tier-2.
   * When extraVariantId is set (add-to-bundle before that line exists in DOM), its tier is included once.
   */
  getEffectiveFreeGameTier(extraVariantId) {
    const tiers = [];
    if (this.drawer) {
      this.drawer.querySelectorAll('.bundle-drawer__cart-item').forEach((item) => {
        if (item.dataset.freeGift === 'true') return;
        const qtyEl = item.querySelector('.bundle-drawer__qty-value');
        const qty = parseInt(qtyEl?.textContent || '1', 10);
        if (qty < 1) return;
        tiers.push(this.normalizeProductTier(item.dataset.productTier));
      });
    }
    if (extraVariantId != null && extraVariantId !== '') {
      const vid = String(extraVariantId);
      const alreadyInPaidCart = this.drawer?.querySelector(
        `.bundle-drawer__cart-item[data-variant-id="${vid}"]:not([data-free-gift="true"])`,
      );
      if (!alreadyInPaidCart) {
        tiers.push(this.normalizeProductTier(this.resolveVariantProductTier(extraVariantId)));
      }
    }
    if (!tiers.length) return 'tier-1';
    return tiers.every((t) => t === 'tier-2') ? 'tier-2' : 'tier-1';
  }

  getTierFilteredFreeGameIds(maxTier) {
    const freeGiftBlock = this.drawer.querySelector('.bundle-drawer__free-gift');
    if (!freeGiftBlock) return [];
    const raw = this.normalizeProductTier(maxTier) === 'tier-2'
      ? (freeGiftBlock.dataset.freeGamesAllVariantIds || '')
      : (freeGiftBlock.dataset.freeGamesTier1AllVariantIds || '');
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }

  getTierFilteredDefaultGameId(maxTier) {
    const freeGiftBlock = this.drawer.querySelector('.bundle-drawer__free-gift');
    if (!freeGiftBlock) return '';
    if (this.normalizeProductTier(maxTier) === 'tier-2') {
      const id = (freeGiftBlock.dataset.freeGamesVariantIds || '').split(',')[0]?.trim();
      return id || '';
    }
    const tier1List = (freeGiftBlock.dataset.freeGamesTier1AllVariantIds || '').split(',').map((s) => s.trim()).filter(Boolean);
    const tier1Set = new Set(tier1List);
    let id = String(freeGiftBlock.dataset.freeGamesTier1DefaultVariantId || '').trim();
    if (id && tier1Set.has(id)) return id;
    if (tier1List.length) return tier1List[0];
    return '';
  }

  getFreeGiftVariantIds(extraVariantId) {
    const freeGiftBlock = this.drawer.querySelector('.bundle-drawer__free-gift');
    if (!freeGiftBlock) return [];
    const effectiveTier = this.normalizeProductTier(this.getEffectiveFreeGameTier(extraVariantId));
    const addonIds = (freeGiftBlock.dataset.freeAddonVariantIds || '').split(',').map((s) => s.trim()).filter(Boolean);
    const gameId = this.getTierFilteredDefaultGameId(effectiveTier);
    const ids = [];
    if (addonIds.length) ids.push(addonIds[0]);
    if (gameId) ids.push(gameId);
    return ids;
  }

  /** Returns "FREE ADD-ON", "FREE GAME", or "FREE GIFT" by variant id */
  getFreeGiftLabel(variantId) {
    const freeGiftBlock = this.drawer.querySelector('.bundle-drawer__free-gift');
    if (!freeGiftBlock) return 'FREE GIFT';
    const id = String(variantId);
    const addonIds = (freeGiftBlock.dataset.freeAddonVariantIds || '').split(',').map((s) => s.trim()).filter(Boolean);
    if (addonIds.includes(id)) return 'FREE ADD-ON';
    const gamesAll = (freeGiftBlock.dataset.freeGamesAllVariantIds || '').split(',').map((s) => s.trim()).filter(Boolean);
    if (gamesAll.includes(id)) return 'FREE GAME';
    return 'FREE GIFT';
  }

  /** Set of all variant IDs that count as free (addon or game from collections) */
  getFreeGiftVariantIdSet() {
    const freeGiftBlock = this.drawer.querySelector('.bundle-drawer__free-gift');
    if (!freeGiftBlock) return new Set();
    const addon = (freeGiftBlock.dataset.freeAddonAllVariantIds || '').split(',').map((s) => s.trim()).filter(Boolean);
    const games = (freeGiftBlock.dataset.freeGamesAllVariantIds || '').split(',').map((s) => s.trim()).filter(Boolean);
    const tier1Games = (freeGiftBlock.dataset.freeGamesTier1AllVariantIds || '').split(',').map((s) => s.trim()).filter(Boolean);
    return new Set([...addon, ...games, ...tier1Games]);
  }

  getFreeAddonVariantIdSet() {
    const freeGiftBlock = this.drawer.querySelector('.bundle-drawer__free-gift');
    if (!freeGiftBlock) return new Set();
    const addon = (freeGiftBlock.dataset.freeAddonAllVariantIds || '').split(',').map((s) => s.trim()).filter(Boolean);
    return new Set(addon);
  }

  getFreeGamesVariantIdSet(maxTier) {
    const freeGiftBlock = this.drawer.querySelector('.bundle-drawer__free-gift');
    if (!freeGiftBlock) return new Set();
    const tier1Only = arguments.length > 0 && this.normalizeProductTier(maxTier) === 'tier-1';
    if (tier1Only) {
      const tier1 = (freeGiftBlock.dataset.freeGamesTier1AllVariantIds || '').split(',').map((s) => s.trim()).filter(Boolean);
      return new Set(tier1);
    }
    const games = (freeGiftBlock.dataset.freeGamesAllVariantIds || '').split(',').map((s) => s.trim()).filter(Boolean);
    return new Set(games);
  }

  isFreeGiftVariant(variantId) {
    if (!variantId) return false;
    return this.getFreeGiftVariantIdSet().has(String(variantId));
  }

  /** Resolve swap popup collection key from cart item */
  getSwapCollectionKeyForItem(variantId, explicitKey) {
    const key = (explicitKey || '').trim();
    if (key === 'free_addons' || key === 'free_games' || key === 'cart_drawer') return key;
    const vid = String(variantId || '');
    if (this.getFreeAddonVariantIdSet().has(vid)) return 'free_addons';
    if (this.getFreeGamesVariantIdSet().has(vid)) return 'free_games';
    return 'cart_drawer';
  }

  initFreeGiftLottie() {
    const container = this.drawer.querySelector('.bundle-drawer__free-gift-lottie');
    if (!container || this.freeGiftLottie || typeof lottie === 'undefined') return;
    const src = container.dataset.lottieSrc;
    if (!src) return;
    this.freeGiftLottie = lottie.loadAnimation({
      container, path: src, renderer: 'svg', loop: false, autoplay: false,
    });
    this.freeGiftLottie.setSpeed(4);
    this.freeGiftLottie.goToAndStop(0, true);
  }

  updateFreeGiftLottie(isUnlocked) {
    this.initFreeGiftLottie();
    if (!this.freeGiftLottie) return;
    const container = this.drawer.querySelector('.bundle-drawer__free-gift-lottie');
    const parentBlock = container?.closest('.bundle-drawer__free-gift') ?? this.drawer.querySelector('.bundle-drawer__free-gift');
    const endFrame = Math.floor(this.freeGiftLottie.totalFrames * this.LOTTIE_PLAY_PERCENT);
    if (isUnlocked) {
      if (this.freeGiftHasBeenLocked) {
        this.freeGiftLottie.playSegments([0, endFrame], true);
      } else {
        this.freeGiftLottie.goToAndStop(endFrame, true);
      }
      if (parentBlock) parentBlock.classList.add('bundle-drawer__free-gift--unlocked');
      if (container) container.classList.add('bundle-drawer__free-gift-lottie--unlocked');
    } else {
      this.freeGiftHasBeenLocked = true;
      this.freeGiftLottie.goToAndStop(0, true);
      if (parentBlock) parentBlock.classList.remove('bundle-drawer__free-gift--unlocked');
      if (container) container.classList.remove('bundle-drawer__free-gift-lottie--unlocked');
    }
  }

  clearFreeGiftTimer() {
    clearTimeout(this.freeGiftAddTimeout);
    this.freeGiftAddTimeout = null;
    this.freeGiftAddScheduled = false;
  }

  hideFreeGiftBlock() {
    const el = this.drawer.querySelector('.bundle-drawer__free-gift');
    if (el) el.classList.add('hidden');
  }

  scheduleFreeGiftAdd() {
    if (this.freeGiftAddScheduled) {
      this._freeGiftLog('scheduleFreeGiftAdd: skipped (already scheduled)');
      return;
    }
    this.freeGiftAddScheduled = true;
    this._freeGiftLog('scheduleFreeGiftAdd: timer scheduled', { delayMs: this.FREE_GIFT_ADD_DELAY_MS });
    this.freeGiftAddTimeout = setTimeout(() => {
      this.freeGiftAddTimeout = null;
      this.freeGiftAddScheduled = false;
      this._freeGiftLog('scheduleFreeGiftAdd: firing addFreeGiftToCart(true)');
      this.addFreeGiftToCart(true);
    }, this.FREE_GIFT_ADD_DELAY_MS);
  }

  scheduleUpdateFreeGiftState(totalItems) {
    this._freeGiftLog('scheduleUpdateFreeGiftState', { totalItems, debounceMs: 180 });
    this.freeGiftStateDebounceTotal = totalItems;
    clearTimeout(this.freeGiftStateDebounceTimer);
    this.freeGiftStateDebounceTimer = setTimeout(() => {
      this.freeGiftStateDebounceTimer = null;
      this.updateFreeGiftState(this.freeGiftStateDebounceTotal);
    }, 180);
  }

  updateFreeGiftState(totalItems) {
    const freeGift = this.drawer.querySelector('.bundle-drawer__free-gift');
    if (!freeGift) {
      this._freeGiftLog('updateFreeGiftState: skip (no .bundle-drawer__free-gift block)', { totalItems });
      return;
    }

    const freeVariantSet = this.getFreeGiftVariantIdSet();
    let paidItems = 0;
    const addonSet = this.getFreeAddonVariantIdSet();
    const gamesSet = this.getFreeGamesVariantIdSet(this.getEffectiveFreeGameTier());
    let hasAddonInCart = false;
    let hasGameInCart = false;
    this.drawer.querySelectorAll('.bundle-drawer__cart-item').forEach((item) => {
      const vid = String(item.dataset.variantId || '');
      if (freeVariantSet.has(vid)) {
        if (addonSet.has(vid)) hasAddonInCart = true;
        if (gamesSet.has(vid)) hasGameInCart = true;
      } else {
        const qtyEl = item.querySelector('.bundle-drawer__qty-value');
        paidItems += parseInt(qtyEl?.textContent || '1', 10);
      }
    });
    const freeGiftInCart = (addonSet.size === 0 || hasAddonInCart) && (gamesSet.size === 0 || hasGameInCart);

    this._freeGiftLog('updateFreeGiftState', {
      totalItems,
      paidItems,
      hasAddonInCart,
      hasGameInCart,
      freeGiftInCart,
      addonSetSize: addonSet.size,
      gamesSetSize: gamesSet.size,
      freeLinesInDom: Array.from(this.drawer.querySelectorAll('.bundle-drawer__cart-item[data-free-gift="true"]')).map((el) => el.dataset.variantId),
    });

    if (freeGiftInCart) {
      this._freeGiftLog('updateFreeGiftState: branch', { branch: 'all_required_free_items_present_hide_block' });
      this.clearFreeGiftTimer();
      this.hideFreeGiftBlock();
      return;
    }

    if (totalItems < 2 || paidItems < 2) {
      this._freeGiftLog('updateFreeGiftState: branch', {
        branch: 'below_threshold',
        totalItems,
        paidItems,
        willRemoveInvalid: paidItems < 2,
      });
      this.clearFreeGiftTimer();
      if (paidItems < 2) this.clearRemovedFreeGiftIds();
      if (paidItems >= 1) {
        freeGift.classList.remove('hidden');
      } else {
        freeGift.classList.add('hidden');
      }
      this.updateFreeGiftLottie(false);
      if (paidItems < 2) this.removeFreeGiftIfInvalid();
      return;
    }

    this._freeGiftLog('updateFreeGiftState: branch', { branch: 'schedule_lottie_and_delayed_add' });
    this.updateFreeGiftLottie(true);
    this.scheduleFreeGiftAdd();
  }

  addFreeGiftToCart(autoAdd = false) {
    const freeGiftBlock = this.drawer.querySelector('.bundle-drawer__free-gift');
    if (!freeGiftBlock) {
      this._freeGiftLog('addFreeGiftToCart: skip', { reason: 'no_free_gift_block' });
      return;
    }
    if (!autoAdd && !freeGiftBlock.classList.contains('bundle-drawer__free-gift--unlocked')) {
      this._freeGiftLog('addFreeGiftToCart: skip', { reason: 'not_unlocked_and_not_auto', autoAdd });
      return;
    }

    let variantIds = this.getFreeGiftVariantIds();
    const removedSet = this.getRemovedFreeGiftIds();
    this._freeGiftLog('addFreeGiftToCart(deferred): start', {
      autoAdd,
      effectiveTier: this.getEffectiveFreeGameTier(),
      variantIdsBeforeFilter: variantIds,
      removedIds: [...removedSet],
    });
    variantIds = variantIds.filter((id) => !removedSet.has(String(id)));
    if (!variantIds.length) {
      this._freeGiftLog('addFreeGiftToCart(deferred): skip', { reason: 'all_variant_ids_filtered_by_removedSet' });
      return;
    }

    const alreadyInDom = new Set(
      Array.from(this.drawer.querySelectorAll('.bundle-drawer__cart-item[data-free-gift="true"]')).map((el) => String(el.dataset.variantId))
    );
    variantIds = variantIds.filter((id) => !alreadyInDom.has(String(id)));
    if (!variantIds.length) {
      this._freeGiftLog('addFreeGiftToCart(deferred): skip', { reason: 'all_variants_already_in_dom' });
      this.hideFreeGiftBlock();
      this.recalcFooter();
      return;
    }

    const addonSet = this.getFreeAddonVariantIdSet();
    const pending = this.getPendingFreeGifts();
    variantIds.forEach((vid) => {
      if (addonSet.has(String(vid))) pending.addon = String(vid);
      else pending.game = String(vid);
    });
    this.setPendingFreeGifts(pending);

    this.injectPendingFreeGiftRows();
    this.hideFreeGiftBlock();
    this.recalcFooter();
    this._freeGiftLog('addFreeGiftToCart(deferred): saved to sessionStorage + DOM', { pending });
  }

  /** Injects or updates free gift rows in the drawer from cart API response */
  injectFreeGiftRowsFromCart(cartData) {
    if (!cartData?.items) return;
    const cartItems = this.drawer.querySelector('.bundle-drawer__cart-items');
    if (!cartItems) return;
    const variantIdSet = this.getFreeGiftVariantIdSet();
    const injectedVariantIds = [];
    cartData.items.forEach((cartItem) => {
      if (!variantIdSet.has(String(cartItem.variant_id))) return;
      const variantId = String(cartItem.variant_id);
      injectedVariantIds.push(variantId);
      const existingEl = Array.from(this.drawer.querySelectorAll('.bundle-drawer__cart-item')).find((el) => String(el.dataset.variantId) === variantId);
      const title = cartItem.title || '';
      const image = (cartItem.featured_image?.url) || (typeof cartItem.image === 'string' ? cartItem.image : cartItem.image?.url) || '';
      const freeLabel = this.getFreeGiftLabel(variantId);
      const freeGiftHtml = `
            <div class="bundle-drawer__cart-item-image">
              <span class="bundle-drawer__cart-item-free-badge">${this.FREE_BADGE_SVG}</span>
              <img src="${image}" alt="${title}" class="bundle-drawer__cart-item-img" width="150" height="150" loading="lazy">
            </div>
            <div class="bundle-drawer__cart-item-details">
              <h4 class="bundle-drawer__cart-item-title">${title}</h4>
              <span class="bundle-drawer__cart-item-free-label">${freeLabel}</span>
              <span class="bundle-drawer__cart-item-qty-static">1 Item</span>
              <div class="bundle-drawer__cart-item-actions">
                <button type="button" class="bundle-drawer__item-action bundle-drawer__item-action--remove">Remove</button>
                <button type="button" class="bundle-drawer__item-action bundle-drawer__item-action--swap">Swap</button>
              </div>
            </div>`;
      if (existingEl) {
        existingEl.classList.add('bundle-drawer__cart-item--free-gift');
        existingEl.dataset.itemPrice = '0';
        existingEl.dataset.freeGift = 'true';
        existingEl.dataset.freeGiftLabel = freeLabel;
        existingEl.innerHTML = freeGiftHtml;
      } else {
        const itemEl = document.createElement('div');
        itemEl.className = 'bundle-drawer__cart-item bundle-drawer__cart-item--free-gift';
        itemEl.dataset.variantId = variantId;
        itemEl.dataset.itemPrice = '0';
        itemEl.dataset.freeGift = 'true';
        itemEl.dataset.freeGiftLabel = freeLabel;
        itemEl.dataset.lineIndex = String(cartItem.line);
        itemEl.innerHTML = freeGiftHtml;
        cartItems.appendChild(itemEl);
        cartItems.classList.remove('hidden');
      }
    });
    this.reindexCartItems();
    this._freeGiftLog('injectFreeGiftRowsFromCart', { injectedVariantIds });
  }

  /**
   * Pending free gifts stored in sessionStorage (not in Shopify Cart).
   * Structure: { addon: "variant_id"|null, game: "variant_id"|null }
   */
  getPendingFreeGifts() {
    try {
      const raw = sessionStorage.getItem(this.STORAGE_KEY_PENDING_FREE_GIFTS);
      if (!raw) return { addon: null, game: null };
      const parsed = JSON.parse(raw);
      return { addon: parsed.addon || null, game: parsed.game || null };
    } catch { return { addon: null, game: null }; }
  }

  setPendingFreeGifts(data) {
    try {
      sessionStorage.setItem(this.STORAGE_KEY_PENDING_FREE_GIFTS, JSON.stringify({
        addon: data.addon || null,
        game: data.game || null,
      }));
    } catch { /* quota exceeded */ }
  }

  clearPendingFreeGifts() {
    try { sessionStorage.removeItem(this.STORAGE_KEY_PENDING_FREE_GIFTS); } catch {}
  }

  hasPendingFreeGifts() {
    const p = this.getPendingFreeGifts();
    return Boolean(p.addon || p.game);
  }

  /**
   * Look up product data (title, image) from swap-collections-data JSON.
   * @param {string} variantId
   * @returns {{ title: string, image: string, label: string }|null}
   */
  getFreeGiftProductData(variantId) {
    const data = this.getSwapCollectionsData();
    if (!data) return null;
    const vid = String(variantId);
    for (const key of ['free_addons', 'free_games']) {
      const list = data[key];
      if (!list) continue;
      const found = list.find((p) => String(p.variant_id) === vid);
      if (found) {
        return {
          title: found.title || '',
          image: found.image || '',
          label: key === 'free_addons' ? 'FREE ADD-ON' : 'FREE GAME',
        };
      }
    }
    return null;
  }

  /**
   * Inject free gift DOM rows from sessionStorage data (no API calls).
   * Uses swap-collections-data for product metadata.
   */
  injectPendingFreeGiftRows() {
    const pending = this.getPendingFreeGifts();
    const cartItems = this.drawer.querySelector('.bundle-drawer__cart-items');
    if (!cartItems) return;

    const vids = [pending.addon, pending.game].filter(Boolean);
    if (!vids.length) return;

    vids.forEach((variantId) => {
      const vid = String(variantId);
      const existing = this.drawer.querySelector(`.bundle-drawer__cart-item[data-variant-id="${vid}"]`);
      if (existing) return;

      const productData = this.getFreeGiftProductData(vid);
      if (!productData) return;

      const freeLabel = productData.label;
      const itemEl = document.createElement('div');
      itemEl.className = 'bundle-drawer__cart-item bundle-drawer__cart-item--free-gift';
      itemEl.dataset.variantId = vid;
      itemEl.dataset.itemPrice = '0';
      itemEl.dataset.freeGift = 'true';
      itemEl.dataset.freeGiftLabel = freeLabel;
      itemEl.dataset.swapCollection = freeLabel === 'FREE ADD-ON' ? 'free_addons' : 'free_games';
      itemEl.innerHTML = `
            <div class="bundle-drawer__cart-item-image">
              <span class="bundle-drawer__cart-item-free-badge">${this.FREE_BADGE_SVG}</span>
              <img src="${productData.image}" alt="${productData.title}" class="bundle-drawer__cart-item-img" width="150" height="150" loading="lazy">
            </div>
            <div class="bundle-drawer__cart-item-details">
              <h4 class="bundle-drawer__cart-item-title">${productData.title}</h4>
              <span class="bundle-drawer__cart-item-free-label">${freeLabel}</span>
              <span class="bundle-drawer__cart-item-qty-static">1 Item</span>
              <div class="bundle-drawer__cart-item-actions">
                <button type="button" class="bundle-drawer__item-action bundle-drawer__item-action--remove">Remove</button>
                <button type="button" class="bundle-drawer__item-action bundle-drawer__item-action--swap">Swap</button>
              </div>
            </div>`;
      cartItems.appendChild(itemEl);
      cartItems.classList.remove('hidden');
    });
    this.reindexCartItems();
  }

  /** Remove all pending free gift DOM rows */
  removePendingFreeGiftRows() {
    const pending = this.getPendingFreeGifts();
    [pending.addon, pending.game].filter(Boolean).forEach((vid) => {
      const el = this.drawer.querySelector(`.bundle-drawer__cart-item[data-variant-id="${vid}"]`);
      if (el) el.remove();
    });
    this.reindexCartItems();
  }

  removeFreeGiftIfInvalid() {
    const pending = this.getPendingFreeGifts();
    if (!pending.addon && !pending.game) return;

    this.removePendingFreeGiftRows();
    this.clearPendingFreeGifts();

    this._freeGiftLog('removeFreeGiftIfInvalid(deferred): cleared storage + DOM');
    this.reindexCartItems();
    this.recalcFooter();
    this.drawer.querySelector('.bundle-drawer__free-gift')?.classList.remove('hidden');
  }

  /**
   * Storage-based tier mismatch check: if effective tier is tier-1 and the
   * pending game in sessionStorage is tier-2-only, swap it to the correct
   * tier-1 game. No API calls.
   */
  fixTierMismatchInStorage() {
    const rawTier = this.getEffectiveFreeGameTier();
    const normalized = this.normalizeProductTier(rawTier);
    if (normalized !== 'tier-1') return;

    const pending = this.getPendingFreeGifts();
    if (!pending.game) return;

    const freeGiftBlock = this.drawer?.querySelector('.bundle-drawer__free-gift');
    if (!freeGiftBlock) return;

    const tier1List = (freeGiftBlock.dataset.freeGamesTier1AllVariantIds || '').split(',').map((s) => s.trim()).filter(Boolean);
    const tier1Set = new Set(tier1List);

    if (tier1Set.has(String(pending.game))) return;

    this._freeGiftLog('fixTierMismatchInStorage: tier-2 game in storage, replacing', { currentGame: pending.game });

    const oldEl = this.drawer.querySelector(`.bundle-drawer__cart-item[data-variant-id="${pending.game}"]`);
    if (oldEl) oldEl.remove();

    const correctGameId = this.getTierFilteredDefaultGameId(rawTier);
    const removedSet = this.getRemovedFreeGiftIds();

    if (correctGameId && !removedSet.has(String(correctGameId))) {
      pending.game = String(correctGameId);
      this.setPendingFreeGifts(pending);
      this.injectPendingFreeGiftRows();
      this._freeGiftLog('fixTierMismatchInStorage: replaced with', { newGame: correctGameId });
    } else {
      pending.game = null;
      this.setPendingFreeGifts(pending);
      this._freeGiftLog('fixTierMismatchInStorage: cleared game (no valid replacement)');
    }
    this.reindexCartItems();
    this.recalcFooter();
  }

  /**
   * Cart Item Management
   */

  reindexCartItems() {
    this.drawer.querySelectorAll('.bundle-drawer__cart-item').forEach((item, i) => {
      item.dataset.lineIndex = String(i + 1);
    });
  }

  resetToEmptyState() {
    this.clearRemovedFreeGiftIds();
    this.setProgressTriggerSource('resetToEmptyState');
    if (this.PROGRESS_DEBUG) {
      console.log('[BundleProgress] trigger:resetToEmptyState');
    }
    this.advanceProgress(0);
    const cartItems = this.drawer.querySelector('.bundle-drawer__cart-items');
    if (cartItems) cartItems.classList.add('hidden');
    const freeGift = this.drawer.querySelector('.bundle-drawer__free-gift');
    if (freeGift) freeGift.classList.add('hidden');
    const footer = this.drawer.querySelector('.bundle-drawer__footer');
    if (footer) { footer.classList.add('hidden'); footer.classList.remove('bundle-drawer__footer--loading'); }
    const heading = this.drawer.querySelector('.bundle-drawer__heading');
    if (heading) heading.textContent = 'Choose your first item';
    this.drawer.classList.add('is-empty');
  }

  showCartItem() {
    const cartItems = this.drawer.querySelector('.bundle-drawer__cart-items');
    if (!cartItems) return null;
    const activeSlide = this.drawer.querySelector('.bundle-drawer__thumb--active')?.closest('.bundle-drawer__slide');
    if (!activeSlide) return null;

    const title = activeSlide.dataset.productTitle || '';
    const price = activeSlide.dataset.productPrice || '';
    const priceRaw = activeSlide.dataset.productPriceRaw || '0';
    const image = activeSlide.dataset.productImage || '';
    const variantId = activeSlide.dataset.variantId || '';

    const priceWithoutUSD = price.replace(' USD', '');

    const itemEl = document.createElement('div');
    itemEl.className = 'bundle-drawer__cart-item';
    itemEl.dataset.variantId = variantId;
    itemEl.dataset.itemPrice = priceRaw;
    itemEl.dataset.productTier = this.normalizeProductTier(activeSlide.dataset.productTier);
    itemEl.dataset.swapCollection = 'cart_drawer';
    itemEl.innerHTML = `
      <div class="bundle-drawer__cart-item-image">
        <img src="${image}" alt="${title}" class="bundle-drawer__cart-item-img" width="150" height="150" loading="lazy">
      </div>
      <div class="bundle-drawer__cart-item-details">
        <h4 class="bundle-drawer__cart-item-title">${title}</h4>
        <span class="bundle-drawer__cart-item-price">${priceWithoutUSD}</span>
        <div class="bundle-drawer__cart-item-qty">
          <button type="button" class="bundle-drawer__qty-btn bundle-drawer__qty-btn--minus" aria-label="Decrease quantity">−</button>
          <span class="bundle-drawer__qty-value">1</span>
          <button type="button" class="bundle-drawer__qty-btn bundle-drawer__qty-btn--plus" aria-label="Increase quantity">+</button>
        </div>
        <div class="bundle-drawer__cart-item-actions">
          <button type="button" class="bundle-drawer__item-action bundle-drawer__item-action--remove">Remove</button>
          <button type="button" class="bundle-drawer__item-action bundle-drawer__item-action--swap">Swap</button>
        </div>
      </div>`;

    const firstFreeGift = cartItems.querySelector('.bundle-drawer__cart-item--free-gift');
    if (firstFreeGift) {
      cartItems.insertBefore(itemEl, firstFreeGift);
    } else {
      cartItems.appendChild(itemEl);
    }
    cartItems.classList.remove('hidden');

    const freeGift = this.drawer.querySelector('.bundle-drawer__free-gift');
    if (freeGift) freeGift.classList.remove('hidden');

    this.reindexCartItems();

    const heading = this.drawer.querySelector('.bundle-drawer__heading');
    if (heading) heading.textContent = 'Choose your second item';
    return itemEl;
  }

  /**
   * Cart Item Actions (delegated click on cart items)
   */

  bindCartItemActions() {
    const container = this.drawer.querySelector('.bundle-drawer__cart-items');
    if (!container || container.dataset.boundActions === 'true') return;
    container.dataset.boundActions = 'true';

    container.addEventListener('click', (event) => {
      const target = event.target.closest('button');
      if (!target) return;

      const cartItem = target.closest('.bundle-drawer__cart-item');
      if (!cartItem) return;

      const lineIndex = parseInt(cartItem.dataset.lineIndex, 10);
      if (!lineIndex) return;

      const qtyEl = cartItem.querySelector('.bundle-drawer__qty-value');
      const currentQty = parseInt(qtyEl?.textContent || '1', 10);

      if (target.classList.contains('bundle-drawer__item-action--remove')) {
        this.showRemoveConfirm(lineIndex, cartItem.dataset.variantId || '');
        return;
      }

      if (target.classList.contains('bundle-drawer__item-action--swap')) {
        const variantId = cartItem.dataset.variantId || '';
        const swapCollectionKey = this.getSwapCollectionKeyForItem(variantId, cartItem.dataset.swapCollection);
        const isFreeGift = swapCollectionKey === 'free_addons' || swapCollectionKey === 'free_games';
        this.openSwapPopup(lineIndex, variantId, swapCollectionKey, isFreeGift ? 'true' : '');
        return;
      }

      if (target.classList.contains('bundle-drawer__qty-btn--plus')) {
        if (this.drawer.classList.contains('loaded')) return;
        const vid = cartItem.dataset.variantId;
        const newQty = currentQty + 1;
        if (qtyEl) qtyEl.textContent = String(newQty);
        this.recalcFooter();
        const key = `qty-${lineIndex}`;
        clearTimeout(this.qtyDebounceTimers[key]);
        this.qtyDebounceTimers[key] = setTimeout(() => {
          if (this.isCartBusy()) return;
          this.lockCart();
          this.setFooterLoading(true);
          this.cartBatchUpdate({ [vid]: newQty })
            .then((data) => {
              this.emitCartUpdated('bundle-drawer:qty increase', data);
              this.updateCartBubble(this.getPaidItemCount(data));
              this.scheduleUpdateFreeGiftState(this.getTotalCartItems());
            })
            .catch((err) => {
              console.error('[Cart] Qty increase error:', err);
              if (qtyEl) qtyEl.textContent = String(currentQty);
              this.recalcFooter();
            })
            .finally(() => { this.setFooterLoading(false); this.unlockCart(); });
        }, 400);
        return;
      }

      if (target.classList.contains('bundle-drawer__qty-btn--minus')) {
        if (currentQty <= 1) {
          this.showRemoveConfirm(lineIndex, cartItem.dataset.variantId || '');
          return;
        }
        if (this.drawer.classList.contains('loaded')) return;
        const vid = cartItem.dataset.variantId;
        const newQty = currentQty - 1;
        if (qtyEl) qtyEl.textContent = String(newQty);
        this.recalcFooter();
        const key = `qty-${lineIndex}`;
        clearTimeout(this.qtyDebounceTimers[key]);
        this.qtyDebounceTimers[key] = setTimeout(() => {
          if (this.isCartBusy()) return;
          this.lockCart();
          this.setFooterLoading(true);
          let paidAfter = 0;
          this.drawer.querySelectorAll('.bundle-drawer__cart-item').forEach((el) => {
            if (el.dataset.freeGift !== 'true') {
              const elVid = el.dataset.variantId;
              const q = parseInt(el.querySelector('.bundle-drawer__qty-value')?.textContent || '1', 10);
              paidAfter += (elVid === vid) ? newQty : q;
            }
          });
          if (paidAfter < 2) {
            this.removePendingFreeGiftRows();
            this.clearPendingFreeGifts();
            this.clearRemovedFreeGiftIds();
          }
          this.cartBatchUpdate({ [vid]: newQty })
            .then((data) => {
              this.emitCartUpdated('bundle-drawer:qty decrease', data);
              this.updateCartBubble(this.getPaidItemCount(data));
              if ((data?.item_count ?? 0) === 0) this.resetToEmptyState();
              this.recalcFooter();
              this.fixTierMismatchInStorage();
              this.scheduleUpdateFreeGiftState(this.getTotalCartItems());
            })
            .catch((err) => {
              console.error('[Cart] Qty decrease error:', err);
              if (qtyEl) qtyEl.textContent = String(currentQty);
              this.recalcFooter();
            })
            .finally(() => { this.setFooterLoading(false); this.unlockCart(); });
        }, 400);
      }
    });
  }

  /**
   * Add-to-Bundle Form
   */

  bindAddToBundle() {
    const form = this.drawer.querySelector('.bundle-drawer__add-form');
    if (!form || form.dataset.boundSubmit === 'true') return;

    const addBtn = form.querySelector('.bundle-drawer__add-btn');
    const spinner = form.querySelector('.loading__spinner');
    const variantInput = form.querySelector('.product-variant-id');
    if (!addBtn || !spinner || !variantInput) return;

    form.dataset.boundSubmit = 'true';

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (addBtn.disabled || variantInput.disabled || !variantInput.value) return;
      if (this.isCartBusy()) return;

      const variantId = parseInt(variantInput.value, 10);
      let paidCount = 0;
      this.drawer.querySelectorAll('.bundle-drawer__cart-item').forEach((item) => {
        if (item.dataset.freeGift !== 'true') paidCount += parseInt(item.querySelector('.bundle-drawer__qty-value')?.textContent || '1', 10);
      });
      const items = [{ id: variantId, quantity: 1 }];

      this._freeGiftLog('addToBundle: cartAdd payload (paid only)', {
        paidCountBeforeAdd: paidCount,
        variantId,
        items,
      });

      this.lockCart();
      addBtn.disabled = true;
      addBtn.classList.add('loading');
      spinner.classList.remove('hidden');
      const addedItemEl = this.showCartItem();
      this.setFooterLoading(true);
      this.recalcFooter();

      this.cartAdd(items, 'add to bundle')
        .then((cartData) => {
          this.recalcFooter();
          if (cartData?.item_count != null) this.updateCartBubble(this.getPaidItemCount(cartData));
          if (addedItemEl && addedItemEl.isConnected) {
            addedItemEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          this.fixTierMismatchInStorage();
          this.scheduleUpdateFreeGiftState(this.getTotalCartItems());
        })
        .catch((error) => {
          console.error('[Cart] Add to bundle error:', error);
        })
        .finally(() => {
          spinner.classList.add('hidden');
          addBtn.classList.remove('loading');
          this.setFooterLoading(false);
          if (this.drawer.classList.contains('is-empty')) this.drawer.classList.remove('is-empty');
          if (!variantInput.disabled) addBtn.disabled = false;
          this.unlockCart();
        });
    });
  }

  /**
   * Bundle Slider (Swiper)
   */

  initBundleSlider() {
    const sliderRow = this.drawer.querySelector('.bundle-drawer__slider-row');
    if (!sliderRow || typeof Swiper === 'undefined') return;
    if (sliderRow.dataset.swiperInit === 'true') return;

    const slider = sliderRow.querySelector('.bundle-drawer__slider');
    const prevBtn = sliderRow.querySelector('.bundle-drawer__slider-btn--prev');
    const nextBtn = sliderRow.querySelector('.bundle-drawer__slider-btn--next');
    const productInfo = this.drawer.querySelector('.bundle-drawer__product-info');
    const titleEl = productInfo?.querySelector('.bundle-drawer__product-title');
    const priceEl = productInfo?.querySelector('.bundle-drawer__product-price');
    const productForm = this.drawer.querySelector('.bundle-drawer__product-form');
    const variantInput = productForm?.querySelector('.product-variant-id');
    const addBtn = productForm?.querySelector('.bundle-drawer__add-btn');
    const addBtnText = addBtn?.querySelector('.bundle-drawer__add-btn-text');

    if (!slider || !productForm || !variantInput) return;

    sliderRow.dataset.swiperInit = 'true';

    const updateActiveSlide = (swiper) => {
      const activeSlide = swiper.slides[swiper.activeIndex];
      if (!activeSlide) return;

      sliderRow.querySelectorAll('.bundle-drawer__thumb').forEach((t) => t.classList.remove('bundle-drawer__thumb--active'));
      const activeThumb = activeSlide.querySelector('.bundle-drawer__thumb');
      if (activeThumb) activeThumb.classList.add('bundle-drawer__thumb--active');

      if (titleEl) titleEl.textContent = activeSlide.dataset.productTitle || '';
      if (priceEl) priceEl.textContent = activeSlide.dataset.productPrice || '';

      const vid = activeSlide.dataset.variantId;
      const available = activeSlide.dataset.available !== 'false';
      if (vid) {
        variantInput.value = vid;
        variantInput.disabled = !available;
        if (addBtn) {
          addBtn.disabled = !available;
          if (addBtnText) addBtnText.textContent = available ? 'ADD TO BUNDLE' : (window.variantStrings?.soldOut || 'Sold out');
        }
      }
    };

    if (this.swiperInstance) { this.swiperInstance.destroy(true, true); this.swiperInstance = null; }

    this.swiperInstance = new Swiper(slider, {
      loop: true, grabCursor: true, centeredSlides: true, slidesPerView: 'auto',
      spaceBetween: 8, slideToClickedSlide: true, observer: true, observeParents: true,
      navigation: { nextEl: nextBtn, prevEl: prevBtn },
      on: {
        slideChange(sw) { updateActiveSlide(sw); },
        afterInit(sw) { updateActiveSlide(sw); },
      },
    });

    this.bindAddToBundle();
    this.bindCartItemActions();
  }

  /**
   * Single-Item Checkout Popup
   */

  getTotalCartItems() {
    let total = 0;
    this.drawer.querySelectorAll('.bundle-drawer__cart-item').forEach((item) => {
      const qtyEl = item.querySelector('.bundle-drawer__qty-value');
      total += item.dataset.freeGift === 'true' ? 1 : parseInt(qtyEl?.textContent || '1', 10);
    });
    return total;
  }

  showSingleItemPopup() {
    if (!this.singleItemPopup) return;
    this.setSingleItemPopupSeen();
    this.singleItemPopup.classList.remove('hidden');
    this.singleItemPopup.setAttribute('aria-hidden', 'false');
  }

  closeSingleItemPopup() {
    if (!this.singleItemPopup) return;
    this.singleItemPopup.classList.add('hidden');
    this.singleItemPopup.setAttribute('aria-hidden', 'true');
  }

  /**
   * Remove Confirmation Popup
   */

  showRemoveConfirm(lineIndex, variantId) {
    if (!this.removeConfirmEl) return;
    this.removeConfirmEl.dataset.pendingLineIndex = String(lineIndex);
    this.removeConfirmEl.dataset.pendingCartItemId = variantId;
    this.removeConfirmEl.classList.remove('hidden');
    this.removeConfirmEl.setAttribute('aria-hidden', 'false');
  }

  closeRemoveConfirm() {
    if (!this.removeConfirmEl) return;
    this.removeConfirmEl.classList.add('hidden');
    this.removeConfirmEl.setAttribute('aria-hidden', 'true');
    delete this.removeConfirmEl.dataset.pendingLineIndex;
    delete this.removeConfirmEl.dataset.pendingCartItemId;
  }

  performRemoveItem(lineIndex) {
    if (this.isCartBusy()) return;
    this.lockCart();
    const cartItem = this.drawer.querySelector(`.bundle-drawer__cart-item[data-line-index="${lineIndex}"]`);
    if (!cartItem) { this.unlockCart(); return; }
    const variantId = cartItem.dataset.variantId;
    const isFreeGift = cartItem.dataset.freeGift === 'true';

    if (isFreeGift) {
      if (variantId) this.addRemovedFreeGiftId(variantId);
      const pending = this.getPendingFreeGifts();
      if (String(pending.addon) === String(variantId)) pending.addon = null;
      if (String(pending.game) === String(variantId)) pending.game = null;
      this.setPendingFreeGifts(pending);
      cartItem.remove();
      this.reindexCartItems();
      this.recalcFooter();
      this.drawer.querySelector('.bundle-drawer__free-gift')?.classList.remove('hidden');
      this.scheduleUpdateFreeGiftState(this.getTotalCartItems());
      this.unlockCart();
      return;
    }

    const qty = parseInt(cartItem.querySelector('.bundle-drawer__qty-value')?.textContent || '1', 10);
    let paidCount = 0;
    this.drawer.querySelectorAll('.bundle-drawer__cart-item').forEach((item) => {
      if (item.dataset.freeGift !== 'true') paidCount += parseInt(item.querySelector('.bundle-drawer__qty-value')?.textContent || '1', 10);
    });
    const newPaidCount = paidCount - qty;

    if (newPaidCount < 2) {
      this.removePendingFreeGiftRows();
      this.clearPendingFreeGifts();
      this.clearRemovedFreeGiftIds();
    }

    this._freeGiftLog('performRemoveItem', { lineIndex, variantId, qty, paidCount, newPaidCount });

    cartItem.style.opacity = '0.5';
    cartItem.style.pointerEvents = 'none';
    this.setFooterLoading(true);

    this.cartBatchUpdate({ [String(variantId)]: 0 })
      .then((data) => {
        this.emitCartUpdated('bundle-drawer:remove item', data);
        cartItem.remove();
        this.reindexCartItems();
        this.updateCartBubble(this.getPaidItemCount(data));
        if ((data?.item_count ?? 0) === 0) this.resetToEmptyState();
        else this.recalcFooter();
        this.fixTierMismatchInStorage();
        this.scheduleUpdateFreeGiftState(this.getTotalCartItems());
      })
      .catch((err) => {
        console.error('[Cart] Remove item error:', err);
        cartItem.style.opacity = '1';
        cartItem.style.pointerEvents = '';
      })
      .finally(() => { this.setFooterLoading(false); this.unlockCart(); });
  }

  /**
   * Swap Popup
   */

  getSwapCollectionsData() {
    if (this.swapCollectionsData) return this.swapCollectionsData;
    const el = document.getElementById('swap-collections-data');
    if (!el) return null;
    try { this.swapCollectionsData = JSON.parse(el.textContent); return this.swapCollectionsData; }
    catch { return null; }
  }

  openSwapPopup(lineIndex, currentVariantId, swapCollectionKey, isFreeGift) {
    const data = this.getSwapCollectionsData();
    if (!data || !this.swapPopup) return;
    let products = data[swapCollectionKey];
    if (!products?.length) return;

    if (swapCollectionKey === 'free_games') {
      const effectiveTier = this.getEffectiveFreeGameTier();
      if (this.normalizeProductTier(effectiveTier) === 'tier-1') {
        products = products.filter((p) => this.normalizeProductTier(p.product_tier) !== 'tier-2');
      }
      if (!products.length) return;
    }

    this.swapPopup.dataset.swapLineIndex = String(lineIndex);
    this.swapPopup.dataset.swapOldVariantId = String(currentVariantId);
    this.swapPopup.dataset.swapCollectionKey = swapCollectionKey;
    this.swapPopup.dataset.swapIsFreeGift = isFreeGift === 'true' ? 'true' : '';

    let swapLineQty = 1;
    if (isFreeGift === 'true') {
      swapLineQty = 1;
    } else {
      const lineEl = this.drawer?.querySelector(`.bundle-drawer__cart-item[data-line-index="${lineIndex}"]`);
      const qEl = lineEl?.querySelector('.bundle-drawer__qty-value');
      const parsed = parseInt(qEl?.textContent || '1', 10);
      swapLineQty = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
    }
    this.swapPopup.dataset.swapLineQty = String(swapLineQty);

    const titleEl = document.getElementById('swap-item-popup-title');
    const subtitleEl = document.getElementById('swap-item-popup-subtitle');
    if (isFreeGift === 'true') {
      if (titleEl) titleEl.textContent = 'SWAP FREE ITEM';
      if (subtitleEl) subtitleEl.textContent = "Choose the item you want instead. Don't worry, it's still free.";
    } else {
      if (titleEl) titleEl.textContent = 'SWAP ITEM';
      if (subtitleEl) subtitleEl.textContent = 'Choose the game you want to switch to below.';
    }

    this.swapSelectedVariantId = null;
    this.swapSelectedProduct = null;
    const swapSelectedBlock = document.getElementById('swap-item-selected');
    const swapConfirmBtn = document.getElementById('swap-item-confirm-btn');
    if (swapSelectedBlock) swapSelectedBlock.classList.add('hidden');
    if (swapConfirmBtn) swapConfirmBtn.classList.add('hidden');
    if (this.swapSelectedTitleEl) this.swapSelectedTitleEl.textContent = '';
    if (this.swapSelectedPriceEl) this.swapSelectedPriceEl.textContent = '';

    const currentVariantIdStr = String(currentVariantId);
    this.swapSliderEl.innerHTML = '';
    products.forEach((product, index) => {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'swap-item-popup__card';
      card.dataset.variantId = String(product.variant_id);
      card.dataset.index = String(index);

      const isCurrentItem = String(product.variant_id) === currentVariantIdStr;
      if (isCurrentItem) {
        card.classList.add('swap-item-popup__card--current');
        card.disabled = true;
      }
      if (product.available === false) {
        card.classList.add('swap-item-popup__card--sold-out');
        card.disabled = true;
      }

      const img = document.createElement('img');
      img.src = product.image || '';
      img.alt = product.title || '';
      img.className = 'swap-item-popup__card-img';
      card.appendChild(img);

      if (product.available === false) {
        const badge = document.createElement('span');
        badge.className = 'swap-item-popup__card-sold-out';
        badge.textContent = 'SOLD OUT';
        card.appendChild(badge);
      }
      if (isCurrentItem) {
        const badge = document.createElement('span');
        badge.className = 'swap-item-popup__card-current';
        badge.textContent = 'CURRENT';
        card.appendChild(badge);
      }

      this.swapSliderEl.appendChild(card);
    });

    const updateSelectedDisplay = () => {
      if (!this.swapSelectedProduct) return;
      if (this.swapSelectedTitleEl) this.swapSelectedTitleEl.textContent = this.swapSelectedProduct.title || '';
      if (this.swapSelectedPriceEl) {
        this.swapSelectedPriceEl.textContent = this.swapSelectedProduct.is_free
          ? (swapCollectionKey === 'free_addons' ? 'FREE ADD-ON!' : 'FREE GAME!')
          : (this.swapSelectedProduct.price || '');
        this.swapSelectedPriceEl.className = 'swap-item-popup__selected-price' + (this.swapSelectedProduct.is_free ? ' swap-item-popup__selected-price--free' : '');
      }
      if (swapSelectedBlock) swapSelectedBlock.classList.remove('hidden');
      if (swapConfirmBtn) swapConfirmBtn.classList.remove('hidden');
    };

    this.swapSliderEl.querySelectorAll('.swap-item-popup__card:not(.swap-item-popup__card--sold-out):not(.swap-item-popup__card--current)').forEach((card) => {
      card.addEventListener('click', () => {
        this.swapSliderEl.querySelectorAll('.swap-item-popup__card').forEach((c) => c.classList.remove('swap-item-popup__card--selected'));
        card.classList.add('swap-item-popup__card--selected');
        const idx = parseInt(card.dataset.index, 10);
        this.swapSelectedProduct = products[idx];
        this.swapSelectedVariantId = this.swapSelectedProduct.variant_id;
        updateSelectedDisplay();
      });
    });

    const prevBtn = this.swapPopup.querySelector('.swap-item-popup__arrow--prev');
    const nextBtn = this.swapPopup.querySelector('.swap-item-popup__arrow--next');
    if (prevBtn) prevBtn.onclick = () => this.swapSliderEl.scrollBy({ left: -200, behavior: 'smooth' });
    if (nextBtn) nextBtn.onclick = () => this.swapSliderEl.scrollBy({ left: 200, behavior: 'smooth' });

    const scrollBarFill = document.getElementById('swap-item-scroll-bar-fill');
    const updateScrollBar = () => {
      if (!scrollBarFill) return;
      const maxScroll = this.swapSliderEl.scrollWidth - this.swapSliderEl.clientWidth;
      if (maxScroll <= 0) {
        scrollBarFill.style.width = '100%';
        return;
      }
      const pct = Math.min(1, this.swapSliderEl.scrollLeft / maxScroll);
      scrollBarFill.style.width = `${pct * 100}%`;
    };
    const onScroll = () => requestAnimationFrame(updateScrollBar);
    if (this.swapSliderEl._swapScrollBarHandler) {
      this.swapSliderEl.removeEventListener('scroll', this.swapSliderEl._swapScrollBarHandler);
      this.swapSliderEl.removeEventListener('touchmove', this.swapSliderEl._swapScrollBarHandler);
    }
    if (this.swapSliderEl._swapScrollBarTick) clearInterval(this.swapSliderEl._swapScrollBarTick);
    this.swapSliderEl._swapScrollBarHandler = onScroll;
    this.swapSliderEl.addEventListener('scroll', onScroll, { passive: true });
    this.swapSliderEl.addEventListener('touchmove', onScroll, { passive: true });
    requestAnimationFrame(updateScrollBar);
    const scrollBarTick = setInterval(updateScrollBar, 100);
    this.swapSliderEl._swapScrollBarTick = scrollBarTick;

    this.swapPopup.classList.remove('hidden');
    this.swapPopup.setAttribute('aria-hidden', 'false');
  }

  closeSwapPopup() {
    if (!this.swapPopup) return;
    if (this.swapSliderEl._swapScrollBarTick) {
      clearInterval(this.swapSliderEl._swapScrollBarTick);
      delete this.swapSliderEl._swapScrollBarTick;
    }
    this.swapPopup.classList.add('hidden');
    this.swapPopup.setAttribute('aria-hidden', 'true');
    delete this.swapPopup.dataset.swapLineIndex;
    delete this.swapPopup.dataset.swapOldVariantId;
    delete this.swapPopup.dataset.swapCollectionKey;
    delete this.swapPopup.dataset.swapIsFreeGift;
    delete this.swapPopup.dataset.swapLineQty;
  }

  performSwap(oldVariantId, newVariantId, isFreeGift = false) {
    if (this.isCartBusy()) return Promise.reject(new Error('Cart is busy'));
    if (isFreeGift && oldVariantId) this.addRemovedFreeGiftId(oldVariantId);
    this.clearFreeGiftTimer();

    if (isFreeGift) {
      const pending = this.getPendingFreeGifts();
      const addonSet = this.getFreeAddonVariantIdSet();
      if (addonSet.has(String(newVariantId))) {
        pending.addon = String(newVariantId);
      } else {
        pending.game = String(newVariantId);
      }
      if (String(pending.addon) === String(oldVariantId)) pending.addon = null;
      if (String(pending.game) === String(oldVariantId)) pending.game = null;
      this.setPendingFreeGifts(pending);

      const oldEl = this.drawer.querySelector(`.bundle-drawer__cart-item[data-variant-id="${oldVariantId}"]`);
      if (oldEl) oldEl.remove();
      this.injectPendingFreeGiftRows();
      this.reindexCartItems();
      this.recalcFooter();
      this._freeGiftLog('performSwap(deferred): free gift swapped in storage+DOM', { oldVariantId, newVariantId, pending });
      return Promise.resolve();
    }

    this.lockCart();
    this.setFooterLoading(true);

    let lastItemCount = null;

    let swapQty = 1;
    if (this.swapPopup?.dataset?.swapLineQty != null) {
      const p = parseInt(this.swapPopup.dataset.swapLineQty, 10);
      if (Number.isFinite(p) && p >= 1) swapQty = p;
    }

    return this.cartAdd([{ id: parseInt(newVariantId, 10), quantity: swapQty }], 'swap: add new')
      .then(() => {
        return this.cartBatchUpdate({ [String(oldVariantId)]: 0 });
      })
      .then((changeData) => {
        lastItemCount = this.getPaidItemCount(changeData);
        return this.cartSectionHtml('swap: refresh drawer');
      })
      .then((htmlText) => {
        const doc = new DOMParser().parseFromString(htmlText, 'text/html');
        const newInner = doc.querySelector('.drawer__inner');
        const currentInner = this.drawer.querySelector('.drawer__inner');
        if (newInner && currentInner) currentInner.innerHTML = newInner.innerHTML;

        const cartItems = this.drawer.querySelector('.bundle-drawer__cart-items');
        if (cartItems) cartItems.dataset.boundActions = 'false';
        const sliderRow = this.drawer.querySelector('.bundle-drawer__slider-row');
        if (sliderRow) sliderRow.dataset.swiperInit = 'false';

        this.freeGiftLottie = null;

        this.clearProgressPhaseTimer();
        this.bindCartItemActions();
        this.initBundleSlider();

        this.injectPendingFreeGiftRows();
        this.recalcFooter();

        if (lastItemCount != null) this.updateCartBubble(lastItemCount);
        this.fixTierMismatchInStorage();
        this.scheduleUpdateFreeGiftState(this.getTotalCartItems());
      })
      .finally(() => { this.setFooterLoading(false); this.unlockCart(); });
  }

  /**
   * View Bundle Popup
   */

  syncProgressLayoutForCurrentStep() {
    const progress = document.querySelector('.bundle-drawer__progress');
    if (!progress) return;
    const currentStep = Math.max(0, Math.min(20, parseInt(progress.dataset.step, 10) || 0));
    this.setProgressTriggerSource('window.updateBundleProgressFromCart');
    this.advanceProgress(currentStep);
  }

  scheduleProgressLayoutSync() {
    clearTimeout(this.progressLayoutSyncTimer);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.syncProgressLayoutForCurrentStep();
      });
    });
    this.progressLayoutSyncTimer = setTimeout(() => {
      this.syncProgressLayoutForCurrentStep();
    }, 340);
  }

  /**
   * Animate progress-wrap to min height (132px) over durationMs. Used on mobile for "Keep shopping".
   */
  animateProgressWrapToMinHeight(durationMs = 300) {
    const progressWrap = document.querySelector('.bundle-constructor__progress-wrap');
    if (!progressWrap) return Promise.resolve();
    const minH = getComputedStyle(progressWrap).getPropertyValue('--bundle-progress-wrap-min-height');
    const minPx = minH ? parseInt(minH, 10) : 132;
    const targetPx = Number.isFinite(minPx) ? minPx : 132;
    progressWrap.style.transition = `height ${durationMs}ms ease-out`;
    progressWrap.style.overflow = 'hidden';
    progressWrap.style.height = `${targetPx}px`;
    return new Promise((resolve) => {
      setTimeout(() => {
        progressWrap.style.transition = '';
        progressWrap.style.overflow = '';
        resolve();
      }, durationMs);
    });
  }

  closeViewBundlePopup() {
    if (!this.viewBundlePopupEl) return;
    const isMobile = window.innerWidth < this.DESKTOP_TRAY_MIN_WIDTH;
    if (!isMobile) {
      this.viewBundlePopupEl.classList.add('hidden');
      this.viewBundlePopupEl.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('bundle-tray-open', 'bundle-tray-overlay');
      const progressWrap = document.querySelector('.bundle-constructor__progress-wrap');
      if (progressWrap) {
        progressWrap.classList.remove('is-tray-open', 'is-scroll-active');
        progressWrap.style.height = '';
        progressWrap.style.overflow = '';
      }
    } else {
      document.body.classList.remove('bundle-tray-overlay');
    }
    if (this.viewBundlePopupSwiper) {
      this.viewBundlePopupSwiper.destroy(true, true);
      this.viewBundlePopupSwiper = null;
    }
    this.scheduleProgressLayoutSync();
  }

  initViewBundlePopupLottie(container) {
    if (!container || typeof lottie === 'undefined') return;
    const src = container.dataset.lottieSrc;
    if (!src) return;
    try {
      lottie.loadAnimation({
        container,
        path: src,
        renderer: 'svg',
        loop: false,
        autoplay: false,
      });
    } catch (err) {
      // console.warn('[Cart] View bundle popup lottie init:', err);
    }
  }

  /** Load Lottie in container, play unlock segment, resolve when complete */
  playPopupUnlockLottie(container) {
    return new Promise((resolve) => {
      if (!container || typeof lottie === 'undefined') {
        resolve();
        return;
      }
      const src = container.dataset.lottieSrc;
      if (!src) {
        resolve();
        return;
      }
      try {
        const anim = lottie.loadAnimation({
          container,
          path: src,
          renderer: 'svg',
          loop: false,
          autoplay: false,
        });
        anim.setSpeed(4);
        anim.addEventListener('complete', () => resolve());
        const endFrame = Math.floor(anim.totalFrames * 1);
        anim.playSegments([0, endFrame], true);
      } catch (err) {
        // console.warn('[Cart] View bundle popup unlock lottie:', err);
        resolve();
      }
    });
  }

  loadViewBundlePopupContent({ allowUnlockMutations = true, skipProgressInContent = false } = {}) {
    if (this.PROGRESS_DEBUG) console.log('[BundleTray] loadViewBundlePopupContent', { allowUnlockMutations, skipProgressInContent });
    if (!this.viewBundlePopupEl || !this.viewBundlePopupContentEl) return Promise.resolve();
    const sectionUrl = `${window.location.pathname}${window.location.pathname.includes('?') ? '&' : '?'}section_id=view-bundle-popup`;
    const sectionPromise = fetch(sectionUrl, { credentials: 'same-origin' }).then((r) => r.text());
    const cartPromise = fetch(`${this.ROOT}cart.js`, { credentials: 'same-origin' }).then((r) => r.json()).catch(() => ({}));

    return Promise.all([sectionPromise, cartPromise])
      .then(([html, cart]) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const section =
          doc.querySelector('#shopify-section-view-bundle-popup') ||
          doc.querySelector('.shopify-section .view-bundle-popup__content')?.closest('.shopify-section') ||
          Array.from(doc.querySelectorAll('.shopify-section')).find((s) => s.querySelector('.view-bundle-popup__content')) ||
          doc.querySelector('.shopify-section');
        const inner = section ? section.innerHTML : '';
        this.viewBundlePopupContentEl.innerHTML = inner;

        if (!skipProgressInContent && typeof window.updateBundleProgressFromCart === 'function') {
          if (this.PROGRESS_DEBUG) console.log('[BundleTray] loadViewBundlePopupContent:updateProgress (skipProgressInContent=false)');
          window.updateBundleProgressFromCart(cart);
        } else if (skipProgressInContent && this.PROGRESS_DEBUG) {
          console.log('[BundleTray] loadViewBundlePopupContent:skippedProgress (skipProgressInContent=true)');
        }

        const sliderEl = this.viewBundlePopupEl.querySelector('.view-bundle-popup__slider');
        const prevBtn = this.viewBundlePopupEl.querySelector('.view-bundle-popup__slider-btn--prev');
        const nextBtn = this.viewBundlePopupEl.querySelector('.view-bundle-popup__slider-btn--next');
        const isDesktopTray = window.innerWidth > 990;
        if (this.viewBundlePopupSwiper) {
          this.viewBundlePopupSwiper.destroy(true, true);
          this.viewBundlePopupSwiper = null;
        }
        if (sliderEl && isDesktopTray && typeof Swiper !== 'undefined' && prevBtn && nextBtn) {
          this.viewBundlePopupSwiper = new Swiper(sliderEl, {
            slidesPerView: 'auto',
            spaceBetween: 24,
            centeredSlides: false,
            grabCursor: true,
            navigation: { nextEl: nextBtn, prevEl: prevBtn },
            observer: true,
            observeParents: true,
          });
          const syncPopupSliderPosition = () => {
            if (!this.viewBundlePopupSwiper) return;
            this.viewBundlePopupSwiper.update();
            this.viewBundlePopupSwiper.slideTo(0, 0);
          };
          requestAnimationFrame(() => {
            requestAnimationFrame(syncPopupSliderPosition);
          });
          setTimeout(syncPopupSliderPosition, 100);
        }

        const lockedSlides = this.viewBundlePopupEl.querySelectorAll('.view-bundle-popup__slide--locked');
        const paidCount = (cart.items || []).reduce((s, i) => s + (i.final_price > 0 ? (i.quantity || 1) : 0), 0);
        const delayUnlockMs = skipProgressInContent ? 2000 : 0;

        if (allowUnlockMutations && lockedSlides.length > 0 && paidCount >= 2) {
          if (this.PROGRESS_DEBUG) console.log('[BundleTray] loadViewBundlePopupContent:unlock-start', { lockedCount: lockedSlides.length, paidCount, delayUnlockMs });
          const runUnlock = () => {
            setTimeout(() => {
              Array.from(lockedSlides).forEach((slide) => slide.classList.add('view-bundle-popup__slide--unlocking'));
            }, 200);

            const unlockPromises = Array.from(lockedSlides).map((slide) => {
              const lottieEl = slide.querySelector('.bundle-drawer__free-gift-lottie');
              return this.playPopupUnlockLottie(lottieEl);
            });
            Promise.all(unlockPromises).then(() => {
              setTimeout(() => {
                const variantIds = Array.from(lockedSlides)
                  .map((s) => s.dataset.variantId)
                  .filter(Boolean);
                if (!variantIds.length) return;

                const addonSet = this.getFreeAddonVariantIdSet();
                const pending = this.getPendingFreeGifts();
                variantIds.forEach((vid) => {
                  if (addonSet.has(String(vid))) pending.addon = String(vid);
                  else pending.game = String(vid);
                });
                this.setPendingFreeGifts(pending);

                Array.from(lockedSlides).forEach((slide) => {
                  slide.classList.remove('view-bundle-popup__slide--unlocking');
                  slide.classList.add('view-bundle-popup__slide--unlocked', 'view-bundle-popup__slide--free');
                  const wrap = slide.querySelector('.view-bundle-popup__locked-wrap');
                  if (wrap) {
                    const contentEl = slide.closest('[data-view-bundle-popup-content]');
                    const badge = document.createElement('span');
                    badge.className = 'view-bundle-popup__slide-badge view-bundle-popup__slide-badge--free';
                    badge.textContent = contentEl?.dataset.freeBadgeLabel || 'FREE';
                    wrap.insertBefore(badge, wrap.firstChild);
                  }
                });
                this.injectPendingFreeGiftRows();
                this.recalcFooter();
              }, 500);
            });
          };
          if (delayUnlockMs > 0) setTimeout(runUnlock, delayUnlockMs);
          else runUnlock();
        } else {
          this.viewBundlePopupEl.querySelectorAll('.bundle-drawer__free-gift-lottie').forEach((el) => this.initViewBundlePopupLottie(el));
        }
      })
      .catch((err) => {
        console.error('[Cart] View bundle popup load error:', err);
        this.closeViewBundlePopup();
      });
  }

  isViewBundlePopupOpen() {
    return Boolean(this.viewBundlePopupEl) &&
      !this.viewBundlePopupEl.classList.contains('hidden') &&
      this.viewBundlePopupEl.getAttribute('aria-hidden') !== 'true';
  }

  scheduleOpenViewBundlePopupRefresh(payload = {}) {
    if (!this.isViewBundlePopupOpen()) {
      if (this.PROGRESS_DEBUG) console.log('[BundleTray] scheduleRefresh:skip (popup not open)');
      return;
    }
    if (this.trayJustAutoShown) {
      if (this.PROGRESS_DEBUG) console.log('[BundleTray] scheduleRefresh:skip (trayJustAutoShown)');
      return;
    }
    const eventId = payload?.eventId;
    if (eventId && eventId === this.lastViewBundlePopupSyncEventId) return;
    if (eventId) this.lastViewBundlePopupSyncEventId = eventId;
    if (this.PROGRESS_DEBUG) console.log('[BundleTray] scheduleRefresh:scheduled', { eventId, source: payload?.source });
    clearTimeout(this.viewBundlePopupRefreshTimer);
    this.viewBundlePopupRefreshTimer = setTimeout(() => {
      if (this.PROGRESS_DEBUG) console.log('[BundleTray] scheduleRefresh:fire');
      this.loadViewBundlePopupContent({ allowUnlockMutations: false });
    }, 180);
  }

  openViewBundleTray(opts = {}) {
    if (!this.viewBundlePopupEl || !this.viewBundlePopupContentEl) {
      this.cacheElements();
      if (!this.viewBundlePopupEl || !this.viewBundlePopupContentEl) return;
    }
    const fromAutoShow = Boolean(opts?.fromAutoShow);
    if (this.PROGRESS_DEBUG) console.log('[BundleTray] openViewBundleTray', { fromAutoShow, progressDelayMs: opts?.progressDelayMs });
    if (fromAutoShow) {
      const delayMs = opts?.progressDelayMs ?? 2000;
      this.progressBlockedUntil = Date.now() + delayMs;
      this.progressAutoShowLocked = true;
      if (this.PROGRESS_DEBUG) console.log('[BundleTray] openViewBundleTray:autoShow-block', { delayMs, blockedUntil: this.progressBlockedUntil, autoShowLocked: true });
      clearTimeout(this.progressBlockedStepTimer);
      this.progressBlockedStepTimer = null;
      this.trayJustAutoShown = true;
      clearTimeout(this.trayJustAutoShownTimer);
      this.trayJustAutoShownTimer = setTimeout(() => {
        this.trayJustAutoShown = false;
        this.trayJustAutoShownTimer = null;
      }, 600);
    }
    this.viewBundlePopupContentEl.innerHTML = '<div class="view-bundle-popup__loading" aria-hidden="true"></div>';
    this.viewBundlePopupEl.classList.remove('hidden');
    this.viewBundlePopupEl.setAttribute('aria-hidden', 'false');
    const progressWrap = document.querySelector('.bundle-constructor__progress-wrap');
    if (progressWrap) progressWrap.classList.add('is-tray-open');
    document.body.classList.add('bundle-tray-open');
    document.body.classList.toggle('bundle-tray-overlay', window.innerWidth >= this.DESKTOP_TRAY_MIN_WIDTH);
    this.scheduleProgressLayoutSync();
    this.loadViewBundlePopupContent({ allowUnlockMutations: true, skipProgressInContent: fromAutoShow }).then(() => {
      if (fromAutoShow && window.innerWidth < this.DESKTOP_TRAY_MIN_WIDTH && window.bundleConstructor) {
        const wrap = document.querySelector('.bundle-constructor__progress-wrap');
        if (wrap) window.bundleConstructor.animateProgressWrapToMaxHeight(wrap, 300);
      }
    });
  }

  /**
   * Event Delegation – drawer click + document click
   */

  initEventDelegation() {
    this.drawer.addEventListener('click', (e) => {
      const checkoutBtn = e.target.closest('.bundle-drawer__checkout-btn');
      if (checkoutBtn) {
        e.preventDefault();
        e.stopPropagation();
        if (this.getTotalCartItems() === 1 && !this.hasSingleItemPopupBeenSeen()) {
          this.showSingleItemPopup();
          return;
        }
        const href = checkoutBtn.getAttribute('href') || '/checkout';
        this.validateCartBeforeCheckout().then((ok) => {
          if (ok) this.prepareCheckout(href);
        });
        return;
      }
    });

    document.addEventListener('click', (e) => {
      const rmBtn = e.target.closest('.bundle-drawer__remove-confirm-btn--remove');
      if (rmBtn && this.removeConfirmEl) {
        const li = this.removeConfirmEl.dataset.pendingLineIndex;
        this.closeRemoveConfirm();
        if (li) this.performRemoveItem(parseInt(li, 10));
        return;
      }
      if (e.target.closest('.bundle-drawer__remove-confirm-btn--cancel') || e.target.closest('.bundle-drawer__remove-confirm-overlay')) {
        this.closeRemoveConfirm();
        return;
      }

      if (e.target.closest('.bundle-drawer__single-item-popup-btn--skip')) {
        this.closeSingleItemPopup();
        const href = document.querySelector('.bundle-drawer__checkout-btn')?.getAttribute('href') || '/checkout';
        if (window.cartDrawerBundle) {
          window.cartDrawerBundle.validateCartBeforeCheckout().then((ok) => {
            if (ok) window.cartDrawerBundle.prepareCheckout(href);
          });
        } else {
          window.location.href = href;
        }
        return;
      }
      const primaryBtn = e.target.closest('.bundle-drawer__single-item-popup-btn--primary');
      if (primaryBtn) {
        const redirectUrl = primaryBtn.dataset.redirectUrl;
        this.closeSingleItemPopup();
        if (redirectUrl) window.location.href = redirectUrl;
        return;
      }
      if (e.target.closest('.bundle-drawer__single-item-popup-overlay')) { this.closeSingleItemPopup(); return; }

      if (e.target.closest('.view-bundle-popup__btn--keep-shopping') || e.target.closest('.view-bundle-popup__overlay')) {
        this.setBundleTrayDismissed();
        if (window.innerWidth < this.DESKTOP_TRAY_MIN_WIDTH) {
          this.animateProgressWrapToMinHeight(300).then(() => this.closeViewBundlePopup());
        } else {
          this.closeViewBundlePopup();
        }
        return;
      }

      if (e.target.closest('#swap-item-cancel-btn') || e.target.closest('.swap-item-popup__overlay')) { this.closeSwapPopup(); return; }

      const swapConfirmBtn = e.target.closest('#swap-item-confirm-btn');
      if (swapConfirmBtn && this.swapPopup && !this.swapPopup.classList.contains('hidden')) {
        const oldVariantId = this.swapPopup.dataset.swapOldVariantId;
        const isFreeGift = this.swapPopup.dataset.swapIsFreeGift === 'true';
        if (oldVariantId && this.swapSelectedVariantId) {
          swapConfirmBtn.disabled = true;
          this.performSwap(oldVariantId, this.swapSelectedVariantId, isFreeGift)
            .then(() => this.closeSwapPopup())
            .catch((err) => console.error('[Cart] Swap error:', err))
            .finally(() => { swapConfirmBtn.disabled = false; });
        }
        return;
      }
    });
  }

  /**
   * Drawer Open & Observer
   */

  onDrawerOpen() {
    setTimeout(() => {
      const progress = this.drawer.querySelector('.bundle-drawer__progress');
      if (progress) {
        const initialStep = Math.max(0, Math.min(20, parseInt(progress.dataset.step, 10) || 0));
        this.setProgressTriggerSource('onDrawerOpen');
        if (this.PROGRESS_DEBUG) {
          console.log('[BundleProgress] trigger:onDrawerOpen', { initialStep });
        }
        this.advanceProgress(initialStep);
      }
      this.initBundleSlider();
      this.recalcFooter();
      this.syncCartOnDrawerOpen();
    }, 350);
  }

  /**
   * On drawer open: migrate any free gifts already in cart (from old version)
   * to sessionStorage, then inject pending free gift DOM rows.
   * Uses one API call only if legacy free gifts are found in the real cart.
   */
  syncCartOnDrawerOpen() {
    let paidCount = 0;
    this.drawer.querySelectorAll('.bundle-drawer__cart-item').forEach((el) => {
      if (el.dataset.freeGift !== 'true') {
        paidCount += parseInt(el.querySelector('.bundle-drawer__qty-value')?.textContent || '1', 10);
      }
    });

    if (paidCount < 2) {
      this.removePendingFreeGiftRows();
      this.clearPendingFreeGifts();
      this.clearRemovedFreeGiftIds();
      this.recalcFooter();
      this.scheduleUpdateFreeGiftState(this.getTotalCartItems());
      return;
    }

    const legacyFreeGifts = this.drawer.querySelectorAll('.bundle-drawer__cart-item[data-free-gift="true"]');
    const freeVariantSet = this.getFreeGiftVariantIdSet();
    const addonSet = this.getFreeAddonVariantIdSet();
    const toMigrate = [];

    legacyFreeGifts.forEach((el) => {
      const vid = String(el.dataset.variantId || '');
      if (freeVariantSet.has(vid)) toMigrate.push(vid);
    });

    if (toMigrate.length && !this.hasPendingFreeGifts()) {
      const pending = this.getPendingFreeGifts();
      const updates = {};
      toMigrate.forEach((vid) => {
        if (addonSet.has(vid)) pending.addon = vid;
        else pending.game = vid;
        updates[vid] = 0;
      });
      this.setPendingFreeGifts(pending);
      this._freeGiftLog('syncCartOnDrawerOpen: migrating legacy free gifts to storage', { toMigrate, pending });

      this.cartBatchUpdate(updates).then(() => {
        this._freeGiftLog('syncCartOnDrawerOpen: legacy free gifts removed from cart');
      }).catch(() => {});
    }

    this.fixTierMismatchInStorage();
    if (this.hasPendingFreeGifts()) {
      this.injectPendingFreeGiftRows();
      this.hideFreeGiftBlock();
    }
    this.recalcFooter();
    this.scheduleUpdateFreeGiftState(this.getTotalCartItems());
  }

  initDrawerObserver() {
    this.wasDrawerActive = this.drawer.classList.contains('active');
    this.classObserver = new MutationObserver(() => {
      const isDrawerActive = this.drawer.classList.contains('active');
      if (isDrawerActive && !this.wasDrawerActive) this.onDrawerOpen();
      this.wasDrawerActive = isDrawerActive;
    });
    this.classObserver.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });

    if (this.drawer.classList.contains('active')) this.onDrawerOpen();
  }

  /**
   * Global Exposures & PubSub Listeners
   */

  exposeGlobals() {
    window.updateBundleProgressFromCart = (cart, opts) => {
      if (!cart?.items) return;
      const paidCount = cart.items.reduce((sum, item) => sum + ((item.final_price > 0) ? (item.quantity || 1) : 0), 0);
      if (paidCount === this.lastPaidCountFromCart) return;
      this.lastPaidCountFromCart = paidCount;
      this.setProgressTriggerSource('window.updateBundleProgressFromCart');
      if (this.PROGRESS_DEBUG) {
        console.log('[BundleProgress] trigger:window.updateBundleProgressFromCart', { paidCount, itemCount: cart.item_count ?? null });
      }
      this.advanceProgress(Math.min(paidCount, 20), opts);
    };

    window.getBundleTrayDismissed = () => this.getBundleTrayDismissed();
    window.setBundleTrayDismissed = () => this.setBundleTrayDismissed();

    window.showViewBundlePopup = (opts = {}) => {
      if (this.PROGRESS_DEBUG) console.log('[BundleTray] window.showViewBundlePopup called', { fromAutoShow: opts?.fromAutoShow, progressDelayMs: opts?.progressDelayMs, mobile: window.innerWidth <= this.DESKTOP_TRAY_MIN_WIDTH });
      if (opts?.fromAutoShow && this.getBundleTrayDismissed()) return;
      if (this.checkIfDrawerOpen()) return;
      if (!this.viewBundlePopupEl || !this.viewBundlePopupContentEl) {
        this.cacheElements();
        if (!this.viewBundlePopupEl || !this.viewBundlePopupContentEl) return;
      }
      if (opts?.fromAutoShow) {
        this.openViewBundleTray(opts);
        return;
      }
      if (window.innerWidth <= this.DESKTOP_TRAY_MIN_WIDTH) {
        fetch(`${this.ROOT}cart.js`, { credentials: 'same-origin' })
          .then((r) => r.json())
          .then((cart) => {
            const paidCount = (cart?.items || []).reduce(
              (s, i) => s + (i.final_price > 0 ? (i.quantity || 1) : 0),
              0
            );
            if (paidCount === 0) return;
            this.openViewBundleTray(opts);
          })
          .catch(() => {});
        return;
      }
      this.openViewBundleTray(opts);
    };
  }

  checkIfDrawerOpen() {
    return Boolean(this.drawer) && this.drawer.classList.contains('active');
  }

  initPubSubListeners() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS?.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
        this.scheduleOpenViewBundlePopupRefresh(payload || {});
      });
    }
    document.addEventListener(this.BUNDLE_CART_UPDATED_EVENT, (event) => {
      const detail = event?.detail || {};
      this.scheduleOpenViewBundlePopupRefresh(detail);
    });
  }

  /**
   * Cleanup
   */

  destroy() {
    if (this.classObserver) {
      this.classObserver.disconnect();
    }
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
    if (this.viewBundlePopupSwiper) {
      this.viewBundlePopupSwiper.destroy(true, true);
      this.viewBundlePopupSwiper = null;
    }
    this.clearProgressPhaseTimer();
    this.clearFreeGiftTimer();
    clearTimeout(this.viewBundlePopupRefreshTimer);
    clearTimeout(this.progressLayoutSyncTimer);
    clearTimeout(this.trayJustAutoShownTimer);
    clearTimeout(this.freeGiftStateDebounceTimer);
    clearTimeout(this.progressBlockedStepTimer);
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawerBundle = new CartDrawerBundle();
});
